<template>
  <div class="scholar-detail-page flex-col">
    <div class="scholar-detail-shell flex-col">
      <ScholarHeader />

      <main class="scholar-detail-main">
        <div class="scholar-detail-toolbar">
          <button type="button" class="scholar-back-btn" data-track-text="返回上一页" aria-label="返回上一页" @click="backToSearch">
            <i class="el-icon-back"></i>
            返回上一页
          </button>
        </div>

        <div class="scholar-detail-layout" :class="{ 'with-toc': work }">
          <nav v-if="work" class="scholar-detail-toc" aria-label="文献详情目录">
            <button
              v-for="section in detailTocSections"
              :key="section.id"
              type="button"
              data-track-text="跳转文献详情目录"
              aria-label="跳转文献详情目录"
              @click="scrollToSection(section.id)"
            >
              {{ section.label }}
            </button>
          </nav>

          <section v-loading="loading" class="scholar-detail-card">
            <el-alert
              v-if="errorMessage"
              class="scholar-detail-error"
              type="error"
              :closable="false"
              :title="errorMessage"
              show-icon
            />

            <template v-if="work">
              <div class="scholar-detail-head">
                <div class="scholar-detail-badges-row">
                  <span v-if="work.isOa" class="scholar-oa-badge">OA</span>
                  <span v-if="work.extra && work.extra.hasFulltext" class="scholar-soft-badge">全文可检索</span>
                  <span v-if="work.oaStatus" class="scholar-soft-badge">{{ oaStatusText(work.oaStatus) }}</span>
                  <span v-if="work.type" class="scholar-soft-badge">{{ formatType(work.type) }}</span>
                  <span v-if="work.language" class="scholar-soft-badge">{{ languageText(work.language) }}</span>
                  <span
                    v-if="currentScreeningMark"
                    class="scholar-screening-badge"
                    :class="`is-${currentScreeningMark.decision || 'pending'}`"
                  >
                    初筛 {{ screeningDecisionText(currentScreeningMark.decision) }}
                  </span>
                </div>
                <h1 class="scholar-detail-title-row">{{ work.title || 'Untitled work' }}</h1>
                <div class="scholar-detail-actions">
                  <el-button
                    v-if="work.pdfUrl"
                    class="scholar-download-btn scholar-icon-btn"
                    type="primary"
                    :loading="downloadLoading"
                    title="下载 PDF"
                    data-track-text="下载文献详情PDF"
                    aria-label="下载 PDF"
                    @click="downloadPdf"
                  >
                    <i class="el-icon-download"></i>
                  </el-button>
                  <el-button class="scholar-citation-btn" data-track-text="打开文献详情引用格式" aria-label="打开文献详情引用格式" @click="openCitationDialog">
                    <i class="el-icon-notebook-2"></i>
                    引用
                  </el-button>
                  <el-button
                    class="scholar-save-btn"
                    :loading="savedLoading"
                    data-track-text="收藏取消收藏文献详情"
                    aria-label="收藏取消收藏文献详情"
                    @click="toggleSavedWork"
                  >
                    <i :class="isCurrentWorkSaved ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                    {{ isCurrentWorkSaved ? '已收藏' : '收藏' }}
                  </el-button>
                  <el-button data-track-text="打开文献详情原文" aria-label="打开文献详情原文" @click="openExternal(work.landingPageUrl || doiUrl(work) || providerUrl)">
                    <i class="el-icon-link"></i>
                    打开原文
                  </el-button>
                  <el-popover
                    v-model="researchActionsVisible"
                    trigger="click"
                    placement="bottom-end"
                    width="420"
                    popper-class="scholar-detail-actions-popover"
                  >
                    <div class="scholar-research-actions-panel">
                      <button
                        type="button"
                        class="scholar-research-group-toggle"
                        :aria-expanded="researchActionGroups.screening ? 'true' : 'false'"
                        data-track-text="展开详情页初筛标记"
                        aria-label="展开详情页初筛标记"
                        @click="toggleResearchActionGroup('screening')"
                      >
                        <span><i class="el-icon-finished"></i> 初筛标记</span>
                        <i :class="researchActionGroups.screening ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      </button>
                      <div v-show="researchActionGroups.screening" class="scholar-research-group-body">
                        <button type="button" class="scholar-research-action-item" data-track-text="详情页标记文献纳入" aria-label="详情页标记文献纳入" @click="runDetailMoreCommand('screening:include')">标记为纳入</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="详情页标记文献可能相关" aria-label="详情页标记文献可能相关" @click="runDetailMoreCommand('screening:maybe')">标记为可能相关</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="详情页标记文献待定" aria-label="详情页标记文献待定" @click="runDetailMoreCommand('screening:pending')">标记为待定</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="详情页标记文献排除" aria-label="详情页标记文献排除" @click="runDetailMoreCommand('screening:exclude')">标记为排除并填写原因</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="详情页填写文献备注" aria-label="详情页填写文献备注" @click="runDetailMoreCommand('screening:note')">填写备注</button>
                        <button
                          type="button"
                          class="scholar-research-action-item"
                          :disabled="!currentScreeningMark"
                          data-track-text="详情页清除文献初筛标记"
                          aria-label="详情页清除文献初筛标记"
                          @click="runDetailMoreCommand('screening:clear')"
                        >
                          清除初筛标记
                        </button>
                      </div>

                      <button
                        type="button"
                        class="scholar-research-group-toggle"
                        :aria-expanded="researchActionGroups.library ? 'true' : 'false'"
                        data-track-text="展开详情页引用库导出"
                        aria-label="展开详情页引用库导出"
                        @click="toggleResearchActionGroup('library')"
                      >
                        <span><i class="el-icon-collection"></i> 引用库导出</span>
                        <i :class="researchActionGroups.library ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      </button>
                      <div v-show="researchActionGroups.library" class="scholar-research-group-body">
                        <button type="button" class="scholar-research-action-item" data-track-text="详情页导出BibTeX" aria-label="详情页导出BibTeX" @click="runDetailMoreCommand('library:bibtex')">导出 BibTeX</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="详情页导出RIS" aria-label="详情页导出RIS" @click="runDetailMoreCommand('library:ris')">导出 RIS / EndNote</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="详情页导出CSV元数据" aria-label="详情页导出CSV元数据" @click="runDetailMoreCommand('library:csv')">导出 CSV 元数据</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="详情页导出Markdown清单" aria-label="详情页导出Markdown清单" @click="runDetailMoreCommand('library:markdown')">导出 Markdown 清单</button>
                      </div>

                      <button
                        v-if="expandSearchOptions.length"
                        type="button"
                        class="scholar-research-group-toggle"
                        :aria-expanded="researchActionGroups.expand ? 'true' : 'false'"
                        data-track-text="展开详情页扩展检索"
                        aria-label="展开详情页扩展检索"
                        @click="toggleResearchActionGroup('expand')"
                      >
                        <span><i class="el-icon-search"></i> 扩展检索</span>
                        <i :class="researchActionGroups.expand ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      </button>
                      <div v-if="expandSearchOptions.length" v-show="researchActionGroups.expand" class="scholar-research-group-body">
                        <button
                          v-for="item in expandSearchOptions"
                          :key="item.key"
                          type="button"
                          class="scholar-research-action-item is-two-line"
                          data-track-text="执行详情页扩展检索"
                          aria-label="执行详情页扩展检索"
                          @click="runDetailMoreCommand(`expand:${item.key}`)"
                        >
                          <strong>{{ item.label }}</strong>
                          <em v-if="item.hint">{{ item.hint }}</em>
                        </button>
                      </div>

                      <button
                        type="button"
                        class="scholar-research-group-toggle"
                        :aria-expanded="researchActionGroups.material ? 'true' : 'false'"
                        data-track-text="展开详情页调研材料"
                        aria-label="展开详情页调研材料"
                        @click="toggleResearchActionGroup('material')"
                      >
                        <span><i class="el-icon-document-copy"></i> 调研材料</span>
                        <i :class="researchActionGroups.material ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      </button>
                      <div v-show="researchActionGroups.material" class="scholar-research-group-body">
                        <button type="button" class="scholar-research-action-item" data-track-text="复制详情页文献笔记" aria-label="复制详情页文献笔记" @click="runDetailMoreCommand('material:copy-note')">复制文献笔记</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="导出详情页文献笔记" aria-label="导出详情页文献笔记" @click="runDetailMoreCommand('material:download-note')">导出文献笔记</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="复制详情页证据卡" aria-label="复制详情页证据卡" @click="runDetailMoreCommand('material:copy-evidence')">复制证据卡</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="导出详情页证据卡" aria-label="导出详情页证据卡" @click="runDetailMoreCommand('material:download-evidence')">导出证据卡</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="复制详情页调研线索" aria-label="复制详情页调研线索" @click="runDetailMoreCommand('material:copy-brief')">复制调研线索</button>
                        <button type="button" class="scholar-research-action-item" data-track-text="导出详情页调研线索" aria-label="导出详情页调研线索" @click="runDetailMoreCommand('material:download-brief')">导出调研线索</button>
                      </div>
                    </div>
                    <el-button slot="reference" class="scholar-more-btn" data-track-text="打开更多科研操作" aria-label="打开更多科研操作">
                      <i class="el-icon-more-outline"></i>
                      更多科研操作
                      <i class="el-icon-arrow-down el-icon--right"></i>
                    </el-button>
                  </el-popover>
                </div>
              </div>

              <el-alert
                v-if="workQualityWarning"
                class="scholar-quality-alert"
                type="warning"
                :closable="false"
                :title="workQualityWarning"
                show-icon
              />

            <div class="scholar-availability-panel">
              <div
                v-for="item in availabilityCards"
                :key="item.key"
                class="scholar-availability-item"
                :class="`is-${item.status}`"
              >
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
                <em>{{ item.hint }}</em>
              </div>
            </div>

            <div class="scholar-detail-metrics" :class="{ 'has-impact': impactMetric }">
              <div>
                <strong>{{ work.publicationYear || '-' }}</strong>
                <span>发表年份</span>
              </div>
              <div>
                <strong>{{ formatNumber(work.citedByCount || 0) }}</strong>
                <span>引用次数</span>
              </div>
              <div v-if="impactMetric">
                <strong>{{ impactMetric.value }}</strong>
                <span>{{ impactMetric.label }}</span>
              </div>
              <div>
                <strong>
                  <button
                    v-if="work.sourceId"
                    type="button"
                    class="scholar-text-link strong"
                    data-track-text="打开详情页来源实体"
                    aria-label="打开详情页来源实体"
                    @click="openEntityDetail('sources', work.sourceId, work.sourceName)"
                  >
                    {{ work.sourceName || '未知来源' }}
                  </button>
                  <template v-else>{{ work.sourceName || '未知来源' }}</template>
                </strong>
                <span>期刊/来源</span>
              </div>
            </div>

            <div id="detail-evidence" class="scholar-detail-section scholar-evidence-section">
              <div class="scholar-section-head">
                <h2>元数据可用性</h2>
                <div class="scholar-section-actions">
                  <button type="button" data-track-text="复制文献元数据可用性" aria-label="复制文献元数据可用性" @click="copyEvidenceCard">
                    <i class="el-icon-document-copy"></i>
                    复制
                  </button>
                  <button type="button" data-track-text="导出文献元数据可用性" aria-label="导出文献元数据可用性" @click="downloadEvidenceCard">
                    <i class="el-icon-download"></i>
                    导出
                  </button>
                </div>
              </div>
              <div class="scholar-evidence-list">
                <div
                  v-for="item in evidenceRows"
                  :key="item.key"
                  class="scholar-evidence-row"
                  :class="`is-${item.status}`"
                >
                  <span>{{ item.label }}</span>
                  <strong>{{ item.value }}</strong>
                  <em>{{ item.hint }}</em>
                </div>
              </div>
              <p class="scholar-evidence-note">仅基于三方接口已返回元数据汇总，用于快速判断后续阅读、引用和导出的核对重点。</p>
            </div>

            <div v-if="openAccessRows.length" id="detail-open-access" class="scholar-detail-section">
              <div class="scholar-section-head">
                <h2>开放获取位置</h2>
                <div class="scholar-section-actions">
                  <button type="button" data-track-text="复制开放获取位置" aria-label="复制开放获取位置" @click="copyOpenAccessLocations">
                    <i class="el-icon-document-copy"></i>
                    复制
                  </button>
                  <button type="button" data-track-text="导出开放获取位置CSV" aria-label="导出开放获取位置CSV" @click="downloadOpenAccessLocations">
                    <i class="el-icon-download"></i>
                    导出 CSV
                  </button>
                </div>
              </div>
              <div class="scholar-location-table">
                <button
                  v-for="row in openAccessRows"
                  :key="row.key"
                  type="button"
                  data-track-text="打开开放获取位置"
                  aria-label="打开开放获取位置"
                  @click="openExternal(row.url)"
                >
                  <span class="scholar-location-source">
                    <strong>{{ row.sourceName }}</strong>
                    <em>{{ row.sourceType }}</em>
                  </span>
                  <span class="scholar-location-badges">
                    <em v-for="tag in row.tags" :key="tag">{{ tag }}</em>
                  </span>
                  <span>{{ row.versionText || '-' }}</span>
                  <span>{{ row.licenseText || '-' }}</span>
                  <span class="scholar-location-url">{{ row.urlType }}</span>
                </button>
              </div>
              <p class="scholar-evidence-note">优先展示 PDF、最佳开放位置、主位置、DOAJ 和有许可证的信息；链接可用性以三方站点实际访问为准。</p>
            </div>

            <div v-if="sourceDetail || entityLoading" id="detail-source" v-loading="entityLoading" class="scholar-detail-section">
              <h2>来源详情</h2>
              <div v-if="sourceDetail" class="scholar-source-panel">
                <div>
                  <strong>
                    <button type="button" class="scholar-text-link strong" data-track-text="打开来源详情实体" aria-label="打开来源详情实体" @click="openEntityDetail('sources', sourceDetail.id || work.sourceId, sourceDetail.name || work.sourceName)">
                      {{ sourceDetail.name || work.sourceName }}
                    </button>
                  </strong>
                  <span>{{ sourceDetail.hint || sourceExtraText(sourceDetail) || '期刊/来源' }}</span>
                </div>
                <div class="scholar-entity-stats">
                  <span v-if="sourceDetail.worksCount !== null && sourceDetail.worksCount !== undefined">
                    作品 {{ formatNumber(sourceDetail.worksCount) }}
                  </span>
                  <span v-if="sourceDetail.citedByCount !== null && sourceDetail.citedByCount !== undefined">
                    引用 {{ formatNumber(sourceDetail.citedByCount) }}
                  </span>
                  <span v-if="sourceMetric('hIndex')">H-index {{ sourceMetric('hIndex') }}</span>
                  <span v-if="sourceExtra('isInDoaj') === true">DOAJ</span>
                  <span v-if="sourceExtra('isOa') === true">开放来源</span>
                </div>
              </div>
            </div>

            <div id="detail-authors" class="scholar-detail-section">
              <h2>作者与机构</h2>
              <div v-if="work.authors && work.authors.length" class="scholar-author-list">
                <div v-for="author in work.authors" :key="author.id || author.name" class="scholar-author-item">
                  <strong>
                    <button
                      v-if="author.id"
                      type="button"
                      class="scholar-text-link strong"
                      data-track-text="打开详情页作者实体"
                      aria-label="打开详情页作者实体"
                      @click="openEntityDetail('authors', author.id, author.name)"
                    >
                      {{ author.name }}
                    </button>
                    <template v-else>{{ author.name }}</template>
                  </strong>
                  <span v-if="authorInstitutions(author)">{{ authorInstitutions(author) }}</span>
                  <div v-if="entityDetails.authors[author.id]" class="scholar-entity-mini">
                    <em v-if="entityDetails.authors[author.id].worksCount !== null && entityDetails.authors[author.id].worksCount !== undefined">
                      作品 {{ formatNumber(entityDetails.authors[author.id].worksCount) }}
                    </em>
                    <em v-if="entityDetails.authors[author.id].citedByCount !== null && entityDetails.authors[author.id].citedByCount !== undefined">
                      引用 {{ formatNumber(entityDetails.authors[author.id].citedByCount) }}
                    </em>
                    <em v-if="metric(entityDetails.authors[author.id], 'hIndex')">
                      H-index {{ metric(entityDetails.authors[author.id], 'hIndex') }}
                    </em>
                  </div>
                </div>
              </div>
              <p v-else class="scholar-muted">暂无作者信息</p>
            </div>

            <div v-if="work.institutions && work.institutions.length" id="detail-institutions" class="scholar-detail-section">
              <h2>机构详情</h2>
              <div class="scholar-entity-card-list">
                <div v-for="institution in work.institutions" :key="institution.id || institution.name" class="scholar-entity-card">
                  <strong>
                    <button
                      v-if="institution.id"
                      type="button"
                      class="scholar-text-link strong"
                      data-track-text="打开详情页机构实体"
                      aria-label="打开详情页机构实体"
                      @click="openEntityDetail('institutions', institution.id, institution.name)"
                    >
                      {{ institution.name }}
                    </button>
                    <template v-else>{{ institution.name }}</template>
                  </strong>
                  <p>{{ institution.countryCode || '-' }} · {{ formatInstitutionType(institution.type) }}</p>
                  <div v-if="entityDetails.institutions[institution.id]" class="scholar-entity-stats">
                    <span>作品 {{ formatNumber(entityDetails.institutions[institution.id].worksCount || 0) }}</span>
                    <span>引用 {{ formatNumber(entityDetails.institutions[institution.id].citedByCount || 0) }}</span>
                    <span v-if="metric(entityDetails.institutions[institution.id], 'hIndex')">
                      H-index {{ metric(entityDetails.institutions[institution.id], 'hIndex') }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div id="detail-abstract" class="scholar-detail-section">
              <h2>摘要</h2>
              <p v-if="work.abstractText" class="scholar-abstract-full">{{ work.abstractText }}</p>
              <p v-else class="scholar-muted">暂无摘要</p>
            </div>

            <div id="detail-topics" class="scholar-detail-section">
              <h2>主题</h2>
              <div v-if="work.topics && work.topics.length" class="scholar-topic-detail-list">
                <button
                  v-for="topic in work.topics"
                  :key="topic.id || topic.name"
                  type="button"
                  data-track-text="打开详情页主题实体"
                  aria-label="打开详情页主题实体"
                  @click="openEntityDetail('topics', topic.id, topic.name)"
                >
                  <strong>{{ topic.name }}</strong>
                  <span>{{ topic.field || topic.domain || topicDetailHint(topic) }}</span>
                  <em v-if="entityDetails.topics[topic.id]">
                    作品 {{ formatNumber(entityDetails.topics[topic.id].worksCount || 0) }}
                  </em>
                </button>
              </div>
              <p v-else class="scholar-muted">暂无主题</p>
            </div>

            <div id="detail-keywords" class="scholar-detail-section">
              <h2>关键词</h2>
              <div v-if="keywordChips.length" class="scholar-keyword-list">
                <button
                  v-for="keyword in keywordChips"
                  :key="keyword.id || keyword.name"
                  type="button"
                  :disabled="!keyword.id"
                  data-track-text="打开详情页关键词检索"
                  aria-label="打开详情页关键词检索"
                  @click="openKeyword(keyword)"
                >
                  {{ keyword.name || keyword.id }}
                </button>
              </div>
              <p v-else class="scholar-muted">暂无关键词</p>
            </div>

            <div v-if="meshTerms.length" id="detail-mesh" class="scholar-detail-section">
              <h2>MeSH 主题词</h2>
              <div class="scholar-mesh-list">
                <span
                  v-for="term in meshTerms"
                  :key="`${term.descriptorUi || term.descriptorName}-${term.qualifierUi || term.qualifierName || ''}`"
                  :class="{ major: term.majorTopic }"
                >
                  {{ meshTermText(term) }}
                  <em v-if="term.majorTopic">主要主题</em>
                </span>
              </div>
            </div>

            <div id="detail-identifiers" class="scholar-detail-section">
              <h2>标识信息</h2>
              <dl class="scholar-field-list">
                <div v-if="work.doi">
                  <dt>DOI</dt>
                  <dd>{{ work.doi }}</dd>
                </div>
                <div v-if="work.publicationDate">
                  <dt>发表日期</dt>
                  <dd>{{ work.publicationDate }}</dd>
                </div>
                <div v-if="biblioText(work)">
                  <dt>卷期页码</dt>
                  <dd>{{ biblioText(work) }}</dd>
                </div>
                <div v-if="providerId">
                  <dt>{{ providerLabel }} ID</dt>
                  <dd>{{ providerId }}</dd>
                </div>
                <div v-for="identifier in externalIdentifiers" :key="identifier.key">
                  <dt>{{ identifier.label }}</dt>
                  <dd>
                    <button v-if="identifier.url" type="button" class="scholar-text-link strong" data-track-text="打开文献外部标识" aria-label="打开文献外部标识" @click="openExternal(identifier.url)">
                      {{ identifier.value }}
                    </button>
                    <span v-else>{{ identifier.value }}</span>
                  </dd>
                </div>
                <div v-if="indexedInLabels.length">
                  <dt>收录数据库</dt>
                  <dd>
                    <span class="scholar-indexed-list">
                      <em v-for="item in indexedInLabels" :key="item">{{ item }}</em>
                    </span>
                  </dd>
                </div>
                <div v-if="work.extra && work.extra.referencedWorksCount !== undefined">
                  <dt>参考文献数</dt>
                  <dd>{{ formatNumber(work.extra.referencedWorksCount || 0) }}</dd>
                </div>
                <div v-if="work.extra && work.extra.locationsCount !== undefined">
                  <dt>全文位置数</dt>
                  <dd>{{ formatNumber(work.extra.locationsCount || 0) }}</dd>
                </div>
                <div v-if="work.extra && work.extra.hasFulltext !== undefined">
                  <dt>全文检索</dt>
                  <dd>{{ work.extra.hasFulltext ? '可检索' : '不可检索' }}</dd>
                </div>
                <div v-if="collaborationText">
                  <dt>合作规模</dt>
                  <dd>{{ collaborationText }}</dd>
                </div>
                <div v-if="impactDetailText">
                  <dt>规范化影响</dt>
                  <dd>{{ impactDetailText }}</dd>
                </div>
                <div v-if="work.extra && work.extra.isRetracted">
                  <dt>状态</dt>
                  <dd class="scholar-warning-text">已撤稿</dd>
                </div>
                <div v-if="work.extra && work.extra.isParatext">
                  <dt>记录类型</dt>
                  <dd class="scholar-warning-text">非正文内容</dd>
                </div>
              </dl>
            </div>

            <div v-if="countsByYear.length" id="detail-citations" class="scholar-detail-section">
              <div class="scholar-section-head">
                <h2>年度引用趋势</h2>
                <div class="scholar-section-actions">
                  <button type="button" data-track-text="复制年度引用趋势" aria-label="复制年度引用趋势" @click="copyCitationTrend">
                    <i class="el-icon-document-copy"></i>
                    复制数据
                  </button>
                  <button type="button" data-track-text="导出年度引用趋势CSV" aria-label="导出年度引用趋势CSV" @click="downloadCitationTrend">
                    <i class="el-icon-download"></i>
                    导出 CSV
                  </button>
                </div>
              </div>
              <div class="scholar-citation-bars">
                <div v-for="item in countsByYear" :key="item.year" class="scholar-citation-row">
                  <span>{{ item.year }}</span>
                  <em><i :style="{ width: citationPercent(item) }"></i></em>
                  <strong>{{ formatNumber(item.citedByCount || 0) }}</strong>
                </div>
              </div>
            </div>

            <div v-if="grants.length || funders.length || sustainableGoals.length" id="detail-funding" class="scholar-detail-section">
              <h2>资助与主题标签</h2>
              <div v-if="grants.length" class="scholar-chip-list">
                <button
                  v-for="grant in grants"
                  :key="`${grant.id || grant.funderId || grant.funderName}-${grant.awardId || grant.doi || ''}`"
                  type="button"
                  :disabled="!grant.funderId"
                  data-track-text="打开详情页资助机构"
                  aria-label="打开详情页资助机构"
                  @click="openGrantFunder(grant)"
                >
                  {{ [grant.funderName || grant.funderId, grant.awardId].filter(Boolean).join(' · ') }}
                </button>
              </div>
              <div v-if="!grants.length && funders.length" class="scholar-chip-list">
                <button
                  v-for="funder in funders"
                  :key="funder.id || funder.name"
                  type="button"
                  :disabled="!funder.id"
                  data-track-text="打开详情页基金实体"
                  aria-label="打开详情页基金实体"
                  @click="openEntityDetail('funders', funder.id, funder.name)"
                >
                  {{ funder.name || funder.id }}
                </button>
              </div>
              <div v-if="sustainableGoals.length" class="scholar-chip-list">
                <span v-for="goal in sustainableGoals" :key="goal.id || goal.name">
                  {{ goal.name || goal.id }}
                </span>
              </div>
            </div>

            <div id="detail-relations" v-loading="relationsLoading" class="scholar-detail-section">
              <div class="scholar-relation-header">
                <h2>关联文献</h2>
                <div class="scholar-relation-actions">
                  <button
                    v-if="activeRelationSection.items.length"
                    type="button"
                    class="scholar-relation-export-btn"
                    :class="{ active: relationExportVisible }"
                    data-track-text="展开关联文献导出"
                    aria-label="展开关联文献导出"
                    @click="toggleRelationExportPanel"
                  >
                    <i class="el-icon-download"></i>
                    导出
                    <i class="el-icon-arrow-down el-icon--right"></i>
                  </button>
                  <button
                    v-if="activeRelationSection.items.length"
                    type="button"
                    data-track-text="复制关联文献清单"
                    aria-label="复制关联文献清单"
                    @click="copyRelationWorks"
                  >
                    复制清单
                  </button>
                  <button
                    v-if="activeRelationSection.key === 'citedBy' && providerId"
                    type="button"
                    data-track-text="查看全部引用本文文献"
                    aria-label="查看全部引用本文文献"
                    @click="searchCitingWorks"
                  >
                    查看全部引用
                  </button>
                  <button
                    v-if="canLoadMoreRelations"
                    type="button"
                    data-track-text="加载更多关联文献"
                    aria-label="加载更多关联文献"
                    @click="loadMoreRelations"
                  >
                    加载更多
                  </button>
                </div>
              </div>
              <div class="scholar-relation-tabs">
                <button
                  v-for="section in relationSections"
                  :key="section.key"
                  type="button"
                  :class="{ active: activeRelationKey === section.key }"
                  data-track-text="切换关联文献分组"
                  aria-label="切换关联文献分组"
                  @click="activeRelationKey = section.key"
                >
                  <span>{{ section.title }}</span>
                  <em>{{ relationTabCount(section) }}</em>
                </button>
              </div>
              <div
                v-if="activeRelationSection.items.length && relationExportVisible"
                class="scholar-relation-export-panel"
              >
                <strong>导出当前关联文献</strong>
                <div>
                  <button type="button" data-track-text="导出关联文献BibTeX" aria-label="导出关联文献BibTeX" @click="runRelationExportCommand('bibtex')">BibTeX</button>
                  <button type="button" data-track-text="导出关联文献RIS" aria-label="导出关联文献RIS" @click="runRelationExportCommand('ris')">RIS / EndNote</button>
                  <button type="button" data-track-text="导出关联文献GBT7714" aria-label="导出关联文献GBT7714" @click="runRelationExportCommand('gbt7714')">GB/T 7714</button>
                  <button type="button" data-track-text="导出关联文献CSV" aria-label="导出关联文献CSV" @click="runRelationExportCommand('csv')">CSV</button>
                  <button type="button" data-track-text="导出关联文献筛选表CSV" aria-label="导出关联文献筛选表CSV" @click="runRelationExportCommand('screening')">筛选表 CSV</button>
                  <button type="button" data-track-text="导出关联文献Markdown" aria-label="导出关联文献Markdown" @click="runRelationExportCommand('markdown')">Markdown 清单</button>
                </div>
              </div>
              <div class="scholar-relation-panel">
                <p v-if="activeRelationSummary" class="scholar-relation-summary">
                  {{ activeRelationSummary }}
                </p>
                <div
                  v-for="item in activeRelationSection.items"
                  :key="item.workKey"
                  class="scholar-relation-item"
                  role="button"
                  tabindex="0"
                  data-track-text="打开关联文献"
                  aria-label="打开关联文献"
                  @click="openRelatedWork(item)"
                  @keydown.enter.prevent="openRelatedWork(item)"
                  @keydown.space.prevent="openRelatedWork(item)"
                >
                  <div class="scholar-relation-main">
                    <strong>
                      {{ item.title || 'Untitled work' }}
                      <em
                        v-if="screeningMarkForWork(item)"
                        class="scholar-relation-screening-badge"
                        :class="`is-${screeningMarkForWork(item).decision || 'pending'}`"
                      >
                        初筛 {{ screeningDecisionText(screeningMarkForWork(item).decision) }}
                      </em>
                    </strong>
                    <span>
                      {{ [item.publicationYear || '-', item.sourceName || '', `引 ${formatNumber(item.citedByCount || 0)}`].filter(Boolean).join(' · ') }}
                    </span>
                  </div>
                  <div class="scholar-relation-item-actions" data-track-ignore @click.stop>
                    <button
                      type="button"
                      class="scholar-relation-action-btn"
                      :class="{ active: isRelationActionPanelOpen(item, 'citation') }"
                      data-track-text="展开关联文献引用"
                      aria-label="展开关联文献引用"
                      @click="toggleRelationActionPanel(item, 'citation')"
                    >
                      引用
                      <i class="el-icon-arrow-down"></i>
                    </button>
                    <button
                      v-if="item.pdfUrl"
                      type="button"
                      class="scholar-relation-action-btn primary"
                      :disabled="isRelationPdfDownloading(item)"
                      data-track-text="下载关联文献PDF"
                      aria-label="下载关联文献PDF"
                      @click="downloadRelationPdf(item)"
                    >
                      <i class="el-icon-download"></i>
                      PDF
                    </button>
                    <button
                      type="button"
                      class="scholar-relation-screening-btn"
                      :class="{ active: isRelationActionPanelOpen(item, 'screening') }"
                      data-track-text="展开关联文献初筛"
                      aria-label="展开关联文献初筛"
                      @click="toggleRelationActionPanel(item, 'screening')"
                    >
                      初筛
                      <template v-if="screeningMarkForWork(item)">：{{ screeningDecisionText(screeningMarkForWork(item).decision) }}</template>
                      <i class="el-icon-arrow-down"></i>
                    </button>
                    <i class="el-icon-arrow-right scholar-relation-go-icon"></i>
                  </div>
                  <div
                    v-if="isRelationActionPanelOpen(item, 'citation')"
                    class="scholar-relation-action-panel"
                    data-track-ignore
                    @click.stop
                  >
                    <strong>复制引用</strong>
                    <div>
                      <button type="button" data-track-text="复制关联文献GBT引用" aria-label="复制关联文献GBT引用" @click="runRelationCitationCommand(item, 'gbt7714')">GB/T</button>
                      <button type="button" data-track-text="复制关联文献APA引用" aria-label="复制关联文献APA引用" @click="runRelationCitationCommand(item, 'apa')">APA</button>
                      <button type="button" data-track-text="复制关联文献BibTeX引用" aria-label="复制关联文献BibTeX引用" @click="runRelationCitationCommand(item, 'bibtex')">BibTeX</button>
                      <button type="button" data-track-text="复制关联文献RIS引用" aria-label="复制关联文献RIS引用" @click="runRelationCitationCommand(item, 'ris')">RIS / EndNote</button>
                    </div>
                  </div>
                  <div
                    v-if="isRelationActionPanelOpen(item, 'screening')"
                    class="scholar-relation-action-panel"
                    data-track-ignore
                    @click.stop
                  >
                    <strong>初筛标记</strong>
                    <div>
                      <button type="button" data-track-text="标记关联文献纳入" aria-label="标记关联文献纳入" @click="runRelationScreeningCommand(item, 'include')">纳入</button>
                      <button type="button" data-track-text="标记关联文献可能相关" aria-label="标记关联文献可能相关" @click="runRelationScreeningCommand(item, 'maybe')">可能相关</button>
                      <button type="button" data-track-text="标记关联文献待定" aria-label="标记关联文献待定" @click="runRelationScreeningCommand(item, 'pending')">待定</button>
                      <button type="button" data-track-text="标记关联文献排除" aria-label="标记关联文献排除" @click="runRelationScreeningCommand(item, 'exclude')">排除并填写原因</button>
                      <button type="button" data-track-text="填写关联文献备注" aria-label="填写关联文献备注" @click="runRelationScreeningCommand(item, 'note')">填写备注</button>
                      <button type="button" data-track-text="清除关联文献初筛标记" aria-label="清除关联文献初筛标记" :disabled="!screeningMarkForWork(item)" @click="runRelationScreeningCommand(item, 'clear')">清除标记</button>
                    </div>
                  </div>
                </div>
                <p v-if="!activeRelationSection.items.length" class="scholar-muted">
                  暂无{{ activeRelationSection.title }}数据
                </p>
              </div>
            </div>
          </template>

          <el-empty v-else-if="!loading && !errorMessage" description="暂无文献详情"></el-empty>
          </section>
        </div>
      </main>
      <ScholarFooter />

      <el-dialog class="scholar-citation-dialog" :visible.sync="citationVisible" width="760px" title="引用格式">
        <div v-if="work" class="scholar-citation">
          <div class="scholar-citation-title">{{ work.title || 'Untitled work' }}</div>
          <el-radio-group v-model="citationFormat" size="small" class="scholar-citation-tabs">
            <el-radio-button
              v-for="item in citationFormats"
              :key="item.value"
              :label="item.value"
            >
              {{ item.label }}
            </el-radio-button>
          </el-radio-group>
          <pre class="scholar-citation-text">{{ citationText }}</pre>
          <p class="scholar-citation-note">引用格式基于三方元数据生成，投稿前请核对作者、期刊、卷期和页码。</p>
          <div class="scholar-citation-actions">
            <el-button type="primary" data-track-text="复制详情页引用格式" aria-label="复制详情页引用格式" @click="copyCitation">
              <i class="el-icon-document-copy"></i>
              复制
            </el-button>
            <el-button data-track-text="导出详情页引用格式" aria-label="导出详情页引用格式" @click="downloadCitation">
              <i class="el-icon-download"></i>
              导出
            </el-button>
          </div>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import {
  deleteSavedScholarWork,
  downloadScholarWorkPdf,
  getScholarEntity,
  getScholarWork,
  getScholarWorkRelations,
  listSavedScholarWorks,
  saveScholarWork
} from '../tools/api/scholar-api'
import {
  formatCitation,
  formatWorks
} from '../tools/scholar-citation'
import { openExternalWithConfirm } from '../tools/external-link'
import { restoreScholarScroll, safeScholarReturnUrl, scholarReturnScrollTop } from '../tools/scholar-route'
import ScholarHeader from '../components/ScholarHeader.vue'
import ScholarFooter from '../components/ScholarFooter.vue'

export default {
  name: 'ScholarWorkDetail',
  components: {
    ScholarHeader,
    ScholarFooter
  },
  data() {
    return {
      loading: false,
      downloadLoading: false,
      savedLoading: false,
      entityLoading: false,
      relationsLoading: false,
      work: null,
      savedWorks: [],
      entityDetails: {
        authors: {},
        institutions: {},
        source: null,
        topics: {}
      },
      relations: {
        citedBy: [],
        references: [],
        related: []
      },
      relationPdfDownloadingKeys: {},
      screeningMarks: {},
      citationVisible: false,
      citationFormat: 'gbt7714',
      citationFormats: [
        { label: 'GB/T 7714', value: 'gbt7714' },
        { label: 'APA', value: 'apa' },
        { label: 'MLA', value: 'mla' },
        { label: 'BibTeX', value: 'bibtex' },
        { label: 'RIS', value: 'ris' }
      ],
      activeRelationKey: 'citedBy',
      relationSize: 5,
      relationExportVisible: false,
      activeRelationActionKey: '',
      extensionRequestId: 0,
      researchActionsVisible: false,
      researchActionGroups: {
        screening: false,
        library: false,
        expand: false,
        material: false
      },
      errorMessage: ''
    }
  },
  computed: {
    provider() {
      return this.$route.query.provider || 'openalex'
    },
    providerId() {
      return this.$route.query.id || ''
    },
    providerLabel() {
      return this.providerName(this.provider)
    },
    providerUrl() {
      const refs = this.work && this.work.providerRefs ? this.work.providerRefs : []
      const ref = refs.find(item => item.provider === this.provider) || refs[0]
      return ref ? ref.url : ''
    },
    sourceDetail() {
      return this.entityDetails.source
    },
    countsByYear() {
      const values = this.work && this.work.extra ? this.work.extra.countsByYear : []
      return Array.isArray(values) ? values.slice(0, 10) : []
    },
    maxCitationCount() {
      return Math.max(1, ...this.countsByYear.map(item => Number(item.citedByCount || 0)))
    },
    oaLocations() {
      const values = this.work && this.work.extra ? this.work.extra.locations : []
      if (!Array.isArray(values)) {
        return []
      }
      return values.filter(item => item && (item.pdfUrl || item.landingPageUrl)).slice(0, 6)
    },
    openAccessRows() {
      const extra = this.work && this.work.extra ? this.work.extra : {}
      const seen = new Set()
      const rows = []
      const addLocation = (location, role) => {
        if (!location || (!location.pdfUrl && !location.landingPageUrl)) {
          return
        }
        const key = [
          location.pdfUrl || '',
          location.landingPageUrl || '',
          location.sourceId || '',
          location.sourceName || '',
          location.version || '',
          location.license || ''
        ].join('|')
        if (seen.has(key)) {
          const existing = rows.find(item => item.rawKey === key)
          if (existing && role && !existing.roles.includes(role)) {
            existing.roles.push(role)
            existing.tags = this.openAccessLocationTags(existing.location, existing.roles)
            existing.score = this.openAccessLocationScore(existing.location, existing.roles)
          }
          return
        }
        seen.add(key)
        const roles = role ? [role] : []
        const sourceName = location.sourceName || (location.pdfUrl ? 'PDF 开放全文' : '开放全文来源')
        const url = location.pdfUrl || location.landingPageUrl || ''
        rows.push({
          rawKey: key,
          key: `${key}-${rows.length}`,
          location,
          roles,
          sourceName,
          sourceType: this.sourceTypeText(location.sourceType) || location.sourceType || '来源',
          versionText: this.formatLocationVersion(location.version),
          licenseText: location.license ? this.licenseText(location.license) : '',
          url,
          urlType: location.pdfUrl ? 'PDF' : '页面',
          tags: this.openAccessLocationTags(location, roles),
          score: this.openAccessLocationScore(location, roles)
        })
      }
      addLocation(extra.bestOaLocation, 'best')
      addLocation(extra.primaryLocation, 'primary')
      ;(Array.isArray(extra.locations) ? extra.locations : []).forEach(location => addLocation(location, 'location'))
      return rows
        .sort((a, b) => b.score - a.score || a.sourceName.localeCompare(b.sourceName))
        .slice(0, 8)
    },
    grants() {
      const values = this.work && this.work.extra ? this.work.extra.grants : []
      return Array.isArray(values) ? values : []
    },
    funders() {
      const values = this.work && this.work.extra ? this.work.extra.funders : []
      return Array.isArray(values) ? values : []
    },
    sustainableGoals() {
      const values = this.work && this.work.extra ? this.work.extra.sustainableDevelopmentGoals : []
      return Array.isArray(values) ? values : []
    },
    meshTerms() {
      const values = this.work && this.work.extra ? this.work.extra.meshTerms : []
      return Array.isArray(values) ? values : []
    },
    keywordChips() {
      const detailValues = this.work && this.work.extra ? this.work.extra.keywordDetails : []
      if (Array.isArray(detailValues) && detailValues.length) {
        return detailValues
          .filter(item => item && (item.id || item.name))
          .map(item => ({
            id: item.id || '',
            name: item.name || item.id || '',
            score: item.score
          }))
      }
      return (this.work && this.work.keywords ? this.work.keywords : [])
        .filter(Boolean)
        .map(name => ({ id: '', name }))
    },
    relationSections() {
      const extra = this.work && this.work.extra ? this.work.extra : {}
      return [
        { key: 'citedBy', title: '引用本文', items: this.relations.citedBy || [], total: this.numberOrNull(this.work && this.work.citedByCount) },
        { key: 'references', title: '参考文献', items: this.relations.references || [], total: this.numberOrNull(extra.referencedWorksCount) },
        { key: 'related', title: '相关文献', items: this.relations.related || [], total: null }
      ]
    },
    activeRelationSection() {
      return this.relationSections.find(item => item.key === this.activeRelationKey) || this.relationSections[0]
    },
    canLoadMoreRelations() {
      const section = this.activeRelationSection
      return !!section && this.relationSize < 20 && !this.relationsLoading && section.items.length >= this.relationSize
    },
    activeRelationSummary() {
      const section = this.activeRelationSection
      if (!section) {
        return ''
      }
      const loaded = section.items.length
      const total = this.numberOrNull(section.total)
      if (total !== null) {
        if (!loaded && total > 0) {
          return `三方元数据记录 ${this.formatNumber(total)} 条，当前未返回可展示条目`
        }
        return `已展示 ${this.formatNumber(loaded)} / ${this.formatNumber(total)} 条`
      }
      return loaded ? `已展示 ${this.formatNumber(loaded)} 条` : ''
    },
    expandSearchOptions() {
      if (!this.work) {
        return []
      }
      const items = []
      const used = new Set()
      const push = (key, label, hint, query) => {
        if (!key || used.has(key) || !query) {
          return
        }
        used.add(key)
        items.push({
          key,
          label,
          hint,
          query: this.compactQuery({
            sort: 'cited',
            ...query
          })
        })
      }
      if (this.providerId) {
        push('cites-work', '引用本文的文献', '按引用数排序', {
          citesWorkId: this.providerId,
          citesWorkName: this.work.title || this.providerId
        })
      }
      if (this.work.sourceId) {
        push(`source:${this.work.sourceId}`, '同来源高引文献', this.work.sourceName || '', {
          sourceId: this.work.sourceId,
          sourceName: this.work.sourceName || ''
        })
      }
      ;(this.work.authors || []).filter(item => item && item.id).slice(0, 3).forEach(author => {
        push(`author:${author.id}`, `作者作品：${author.name || author.id}`, '查看该作者高引作品', {
          authorId: author.id,
          authorName: author.name || ''
        })
      })
      ;(this.work.topics || []).filter(item => item && item.id).slice(0, 3).forEach(topic => {
        push(`topic:${topic.id}`, `同主题文献：${topic.name || topic.id}`, [topic.field, topic.domain].filter(Boolean).join(' · '), {
          topicId: topic.id,
          topicName: topic.name || ''
        })
      })
      ;(this.work.institutions || []).filter(item => item && item.id).slice(0, 3).forEach(institution => {
        push(`institution:${institution.id}`, `机构作品：${institution.name || institution.id}`, institution.countryCode || '', {
          institutionId: institution.id,
          institutionName: institution.name || ''
        })
      })
      const funderItems = this.grants.length
        ? this.grants.map(item => ({ id: item.funderId, name: item.funderName || item.funderId }))
        : this.funders.map(item => ({ id: item.id, name: item.name || item.id }))
      funderItems.filter(item => item && item.id).slice(0, 3).forEach(funder => {
        push(`funder:${funder.id}`, `基金相关文献：${funder.name || funder.id}`, '按资助机构筛选', {
          funderId: funder.id,
          funderName: funder.name || ''
        })
      })
      this.keywordChips.filter(item => item && item.id).slice(0, 3).forEach(keyword => {
        push(`keyword:${keyword.id}`, `关键词文献：${keyword.name || keyword.id}`, '按关键词筛选', {
          keywordId: keyword.id,
          keywordName: keyword.name || ''
        })
      })
      this.sustainableGoals.filter(item => item && item.id).slice(0, 3).forEach(goal => {
        push(`sdg:${goal.id}`, `SDG 相关文献：${goal.name || goal.id}`, '按可持续发展目标筛选', {
          sdgId: goal.id,
          sdgName: goal.name || ''
        })
      })
      return items
    },
    citationText() {
      return this.work ? formatCitation(this.work, this.citationFormat) : ''
    },
    currentScreeningMark() {
      return this.screeningMarkForWork(this.work)
    },
    isCurrentWorkSaved() {
      return this.isSaved(this.work)
    },
    impactMetric() {
      const extra = this.work && this.work.extra ? this.work.extra : {}
      const percentile = extra.citationNormalizedPercentile || {}
      if (percentile.isInTop1Percent) {
        return { value: 'Top 1%', label: '规范化影响' }
      }
      if (percentile.isInTop10Percent) {
        return { value: 'Top 10%', label: '规范化影响' }
      }
      if (typeof extra.fwci === 'number') {
        return { value: this.formatDecimal(extra.fwci), label: 'FWCI' }
      }
      return null
    },
    impactDetailText() {
      const extra = this.work && this.work.extra ? this.work.extra : {}
      const parts = []
      if (typeof extra.fwci === 'number') {
        parts.push(`FWCI ${this.formatDecimal(extra.fwci)}`)
      }
      const percentile = extra.citationNormalizedPercentile || {}
      if (percentile.isInTop1Percent) {
        parts.push('同领域同年 Top 1%')
      } else if (percentile.isInTop10Percent) {
        parts.push('同领域同年 Top 10%')
      } else if (typeof percentile.value === 'number') {
        parts.push(`规范化百分位 ${this.formatDecimal(percentile.value)}`)
      }
      const yearPercentile = extra.citedByPercentileYear || {}
      if (yearPercentile.min !== undefined || yearPercentile.max !== undefined) {
        parts.push(`同年引用百分位 ${[yearPercentile.min, yearPercentile.max].filter(value => value !== undefined && value !== null).join('-')}`)
      }
      return parts.join(' · ')
    },
    externalIdentifiers() {
      const ids = this.work && this.work.extra && this.work.extra.ids ? this.work.extra.ids : {}
      const items = []
      const add = (key, label, value, url) => {
        if (!value) {
          return
        }
        const displayValue = String(value)
          .replace(/^https?:\/\/openalex\.org\//i, '')
          .replace(/^https?:\/\/pubmed\.ncbi\.nlm\.nih\.gov\//i, '')
          .replace(/^https?:\/\/www\.ncbi\.nlm\.nih\.gov\/pmc\/articles\//i, '')
          .replace(/\/$/i, '')
        if (displayValue && displayValue !== this.providerId && displayValue !== this.work.doi) {
          items.push({ key, label, value: displayValue, url })
        }
      }
      add('pmid', 'PMID', ids.pmid, ids.pmid && String(ids.pmid).startsWith('http') ? ids.pmid : '')
      add('pmcid', 'PMCID', ids.pmcid, ids.pmcid && String(ids.pmcid).startsWith('http') ? ids.pmcid : '')
      add('mag', 'MAG', ids.mag, '')
      add('openalex', 'OpenAlex', ids.openalex, ids.openalex && String(ids.openalex).startsWith('http') ? ids.openalex : '')
      return items
    },
    indexedInLabels() {
      const values = this.work && this.work.extra && Array.isArray(this.work.extra.indexedIn) ? this.work.extra.indexedIn : []
      return values.map(value => this.indexedInText(value)).filter(Boolean)
    },
    collaborationText() {
      const extra = this.work && this.work.extra ? this.work.extra : {}
      const parts = []
      if (extra.countriesDistinctCount !== null && extra.countriesDistinctCount !== undefined) {
        parts.push(`${this.formatNumber(extra.countriesDistinctCount)} 个国家/地区`)
      }
      if (extra.institutionsDistinctCount !== null && extra.institutionsDistinctCount !== undefined) {
        parts.push(`${this.formatNumber(extra.institutionsDistinctCount)} 个机构`)
      }
      return parts.join(' · ')
    },
    workQualityWarning() {
      const extra = this.work && this.work.extra ? this.work.extra : {}
      if (extra.isRetracted) {
        return '该文献已被标记为撤稿，请谨慎引用和使用。'
      }
      if (extra.isParatext) {
        return '该记录属于非正文内容，可能不是正式研究论文。'
      }
      return ''
    },
    availabilityCards() {
      const work = this.work || {}
      const extra = work.extra || {}
      const bestLocation = this.openAccessRows[0] || null
      const hasPdf = !!work.pdfUrl || this.openAccessRows.some(item => item && item.location && item.location.pdfUrl)
      const indexed = this.indexedInLabels.slice(0, 3)
      return [
        {
          key: 'access',
          label: '全文获取',
          value: hasPdf ? '可下载 PDF' : (work.isOa ? '可打开开放位置' : '暂未识别开放全文'),
          hint: hasPdf ? '页面顶部 PDF 图标可直接下载' : (bestLocation ? bestLocation.urlType : '详情接口未返回 PDF'),
          status: hasPdf || work.isOa ? 'ok' : 'missing'
        },
        {
          key: 'oa',
          label: '开放状态',
          value: work.isOa ? this.oaStatusText(work.oaStatus) : '非开放或未知',
          hint: bestLocation && bestLocation.versionText ? bestLocation.versionText : '来自开放获取元数据',
          status: work.isOa ? 'ok' : 'missing'
        },
        {
          key: 'license',
          label: '许可证',
          value: bestLocation && bestLocation.licenseText ? bestLocation.licenseText : '未知',
          hint: bestLocation && bestLocation.sourceName ? bestLocation.sourceName : '接口未返回许可证',
          status: bestLocation && bestLocation.licenseText ? 'ok' : 'warn'
        },
        {
          key: 'location',
          label: '最佳位置',
          value: bestLocation ? bestLocation.sourceName : (work.sourceName || '未知来源'),
          hint: bestLocation ? `${bestLocation.urlType} · ${bestLocation.sourceType}` : '可通过原文链接核对',
          status: bestLocation ? 'ok' : 'warn'
        },
        {
          key: 'indexed',
          label: '收录库',
          value: indexed.length ? indexed.join('、') : '未知',
          hint: this.indexedInLabels.length > 3 ? `等 ${this.indexedInLabels.length} 个` : 'OpenAlex indexed_in',
          status: indexed.length ? 'ok' : 'warn'
        },
        {
          key: 'fulltext',
          label: '全文检索',
          value: extra.hasFulltext ? '可全文检索' : '未返回',
          hint: extra.hasFulltext ? '支持全文相关过滤' : '不代表原文不存在',
          status: extra.hasFulltext ? 'ok' : 'warn'
        }
      ]
    },
    evidenceRows() {
      const work = this.work || {}
      const extra = work.extra || {}
      const hasAbstract = !!this.cleanText(work.abstractText)
      const hasOpenLocation = this.openAccessRows.length > 0
      const hasPdf = !!work.pdfUrl || this.openAccessRows.some(item => item && item.location && item.location.pdfUrl)
      const authorCount = work.authors && work.authors.length ? work.authors.length : 0
      const institutionCount = work.institutions && work.institutions.length ? work.institutions.length : 0
      const topicCount = work.topics && work.topics.length ? work.topics.length : 0
      const keywordCount = this.keywordChips.length
      const referenceCount = this.numberOrNull(extra.referencedWorksCount)
      const locationCount = this.numberOrNull(extra.locationsCount)
      const relationCount = this.relationSections.reduce((sum, section) => sum + ((section.items && section.items.length) || 0), 0)
      const add = (key, label, ok, value, hint, status) => ({
        key,
        label,
        value: value || (ok ? '可用' : '缺失'),
        hint: hint || (ok ? '接口已返回' : '需回原文或其它来源核对'),
        status: status || (ok ? 'ok' : 'missing')
      })
      return [
        add('doi', 'DOI', !!work.doi, work.doi ? '已返回' : '缺 DOI', work.doi || '导出引用前建议核对'),
        add('abstract', '摘要', hasAbstract, hasAbstract ? '已返回' : '暂无摘要', hasAbstract ? `${this.cleanText(work.abstractText).length} 字符` : '详情接口未返回摘要'),
        add('pdf', 'PDF/全文', hasPdf, hasPdf ? '可下载/打开' : '暂无 PDF', hasOpenLocation ? `${this.openAccessRows.length} 个开放位置` : '接口未返回可用 PDF'),
        add('authors', '作者机构', authorCount > 0, authorCount ? `${authorCount} 位作者` : '暂无作者', institutionCount ? `${institutionCount} 个机构` : '机构信息不完整'),
        add('topics', '主题关键词', topicCount || keywordCount, `${topicCount} 主题 / ${keywordCount} 关键词`, topicCount || keywordCount ? '可用于扩展检索' : '接口未返回主题或关键词'),
        add('references', '参考文献', referenceCount !== null && referenceCount > 0, referenceCount !== null ? `${this.formatNumber(referenceCount)} 条` : '未知', referenceCount ? '可查看已加载参考文献' : '详情接口未返回参考文献数量'),
        add('citations', '引用趋势', this.countsByYear.length > 0, this.countsByYear.length ? `${this.countsByYear.length} 年` : '暂无趋势', this.countsByYear.length ? '可导出年度引用 CSV' : '接口未返回年度引用序列'),
        add('relations', '关联文献', relationCount > 0, relationCount ? `${this.formatNumber(relationCount)} 条已加载` : '暂无已加载', '引用本文、参考文献、相关文献'),
        add('identifiers', '外部标识', this.externalIdentifiers.length || this.indexedInLabels.length, `${this.externalIdentifiers.length} 标识 / ${this.indexedInLabels.length} 收录`, 'PMID、PMCID、收录库等'),
        add('risk', '风险标记', !(extra.isRetracted || extra.isParatext), extra.isRetracted || extra.isParatext ? '需谨慎' : '未见风险', this.workQualityWarning || '未返回撤稿或非正文标记', extra.isRetracted || extra.isParatext ? 'warn' : 'ok'),
        add('funding', '资助标签', this.grants.length || this.funders.length || this.sustainableGoals.length, `${this.grants.length || this.funders.length} 资助 / ${this.sustainableGoals.length} SDG`, '可用于基金或主题扩展检索'),
        add('locations', '全文位置数', locationCount !== null && locationCount > 0, locationCount !== null ? `${this.formatNumber(locationCount)} 个` : '未知', locationCount ? '开放位置已由接口返回' : '详情接口未返回全文位置数')
      ]
    },
    detailTocSections() {
      if (!this.work) {
        return []
      }
      return [
        { id: 'detail-evidence', label: '可用性', show: true },
        { id: 'detail-open-access', label: '开放获取', show: this.openAccessRows.length },
        { id: 'detail-source', label: '来源', show: this.sourceDetail || this.entityLoading },
        { id: 'detail-authors', label: '作者', show: true },
        { id: 'detail-institutions', label: '机构', show: this.work.institutions && this.work.institutions.length },
        { id: 'detail-abstract', label: '摘要', show: true },
        { id: 'detail-topics', label: '主题', show: true },
        { id: 'detail-keywords', label: '关键词', show: true },
        { id: 'detail-mesh', label: 'MeSH', show: this.meshTerms.length },
        { id: 'detail-identifiers', label: '标识', show: true },
        { id: 'detail-citations', label: '引用趋势', show: this.countsByYear.length },
        { id: 'detail-funding', label: '资助标签', show: this.grants.length || this.funders.length || this.sustainableGoals.length },
        { id: 'detail-relations', label: '关联文献', show: true }
      ].filter(item => item.show)
    }
  },
  watch: {
    '$route.query': {
      handler() {
        this.scrollToPageTop()
        this.loadWork()
      },
      immediate: true
    },
    activeRelationKey() {
      this.relationExportVisible = false
      this.activeRelationActionKey = ''
    }
  },
  methods: {
    scrollToPageTop() {
      this.$nextTick(() => {
        if (typeof window !== 'undefined' && window.scrollTo) {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
        }
        if (typeof document !== 'undefined') {
          if (document.documentElement) {
            document.documentElement.scrollTop = 0
          }
          if (document.body) {
            document.body.scrollTop = 0
          }
        }
      })
    },
    async loadWork() {
      if (!this.providerId) {
        this.errorMessage = '缺少文献详情 ID'
        this.work = null
        return
      }
      this.loading = true
      this.resetExtensions()
      this.errorMessage = ''
      this.loadScreeningMarks()
      try {
        this.work = await getScholarWork(this.provider, this.providerId)
        this.loadSavedWorks()
        this.loadExtensions()
      } catch (e) {
        this.work = null
        this.errorMessage = this.resolveErrorMessage(e, '文献详情加载失败')
      } finally {
        this.loading = false
      }
    },
    resetExtensions() {
      this.extensionRequestId += 1
      this.entityDetails = {
        authors: {},
        institutions: {},
        source: null,
        topics: {}
      }
      this.relations = {
        citedBy: [],
        references: [],
        related: []
      }
      this.activeRelationKey = 'citedBy'
      this.relationSize = 5
      this.entityLoading = false
      this.relationsLoading = false
    },
    loadExtensions() {
      const requestId = this.extensionRequestId
      this.loadEntityDetails(requestId)
      this.loadRelations(requestId)
    },
    async loadEntityDetails(requestId) {
      if (!this.work) {
        return
      }
      this.entityLoading = true
      try {
        const sourcePromise = this.work.sourceId
          ? getScholarEntity(this.provider, 'sources', this.work.sourceId).catch(() => null)
          : Promise.resolve(null)
        const authorsPromise = this.fetchEntityMap('authors', this.work.authors, 6)
        const institutionsPromise = this.fetchEntityMap('institutions', this.work.institutions, 6)
        const topicsPromise = this.fetchEntityMap('topics', this.work.topics, 8)
        const [source, authors, institutions, topics] = await Promise.all([
          sourcePromise,
          authorsPromise,
          institutionsPromise,
          topicsPromise
        ])
        if (requestId !== this.extensionRequestId) {
          return
        }
        this.entityDetails = {
          source,
          authors,
          institutions,
          topics
        }
      } finally {
        if (requestId === this.extensionRequestId) {
          this.entityLoading = false
        }
      }
    },
    async fetchEntityMap(entityType, items, limit) {
      const ids = (items || [])
        .map(item => item && item.id)
        .filter(Boolean)
        .filter((id, index, array) => array.indexOf(id) === index)
        .slice(0, limit)
      const responses = await Promise.all(ids.map(id =>
        getScholarEntity(this.provider, entityType, id).catch(() => null)
      ))
      return responses.reduce((map, item) => {
        if (item && item.id) {
          map[item.id] = item
        }
        return map
      }, {})
    },
    async loadRelations(requestId) {
      this.relationsLoading = true
      try {
        const relations = await getScholarWorkRelations(this.provider, this.providerId, this.relationSize)
        if (requestId !== this.extensionRequestId) {
          return
        }
        this.relations = {
          citedBy: relations.citedBy || [],
          references: relations.references || [],
          related: relations.related || []
        }
      } catch (e) {
        if (requestId === this.extensionRequestId) {
          this.relations = {
            citedBy: [],
            references: [],
            related: []
          }
        }
      } finally {
        if (requestId === this.extensionRequestId) {
          this.relationsLoading = false
        }
      }
    },
    loadMoreRelations() {
      if (this.relationSize >= 20 || this.relationsLoading) {
        return
      }
      this.relationSize = 20
      this.loadRelations(this.extensionRequestId)
    },
    backToSearch() {
      const returnUrl = safeScholarReturnUrl(this.$route.query.returnUrl)
      if (returnUrl) {
        const returnScroll = scholarReturnScrollTop(this.$route.query.returnScroll)
        this.$router.push(returnUrl).then(() => {
          restoreScholarScroll(returnScroll)
        }).catch(() => {
          restoreScholarScroll(returnScroll)
        })
        return
      }
      if (window.history.length > 1) {
        this.$router.back()
        return
      }
      const query = {}
      if (this.$route.query.q) {
        query.q = this.$route.query.q
      }
      this.$router.push({ path: '/scholar', query })
    },
    toPath(path) {
      this.$router.push(path)
    },
    showIframe(url) {
      const index = this.$authPopup.open(url)
      if (index === null) {
        this.$router.push(url)
      }
      return index
    },
    getScholarAuthUser() {
      const authContext = localStorage.getItem('auth-context')
      if (!authContext) {
        return null
      }
      try {
        const users = JSON.parse(authContext)
        return users.find(item => item && (item.type === 'Person' || item.type === 'Child') && item.name) || null
      } catch (e) {
        return null
      }
    },
    isScholarPersonalUser() {
      return !!this.getScholarAuthUser()
    },
    requireScholarLogin(message) {
      if (this.isScholarPersonalUser()) {
        return true
      }
      this.$message.warning(message || '请先登录个人账号或子账号后使用文献收藏')
      this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
      return false
    },
    openExternal(url) {
      return openExternalWithConfirm(this, url)
    },
    scrollToSection(id) {
      const target = document.getElementById(id)
      if (!target) {
        return
      }
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    async downloadPdf() {
      if (!this.work || !this.work.pdfUrl || this.downloadLoading) {
        return
      }
      this.downloadLoading = true
      try {
        const result = await downloadScholarWorkPdf(this.provider, this.providerId)
        this.saveBlob(result.blob, result.filename || this.defaultPdfFilename())
        this.$message.success('下载已开始')
      } catch (e) {
        await this.handlePdfDownloadFailure(this.work, e, { fallbackUrl: this.work.landingPageUrl || this.doiUrl(this.work) || this.providerUrl })
      } finally {
        this.downloadLoading = false
      }
    },
    pdfFallbackUrl(work) {
      if (!work) {
        return ''
      }
      if (work === this.work) {
        return work.landingPageUrl || this.doiUrl(work) || this.providerUrl
      }
      return this.relationWorkUrl(work) || this.doiUrl(work)
    },
    async handlePdfDownloadFailure(work, error, options = {}) {
      const pdfUrl = work && work.pdfUrl ? work.pdfUrl : ''
      const fallbackUrl = options.fallbackUrl || this.pdfFallbackUrl(work)
      const message = '当前文献暂未返回可直接下载的 PDF，可先复制 PDF 链接，或打开原文页手动下载。'
      if (!pdfUrl && !fallbackUrl) {
        this.$message.warning('当前文献暂未返回可直接下载的 PDF')
        return
      }
      try {
        await this.$confirm(
          message,
          'PDF 下载未完成',
          {
            confirmButtonText: pdfUrl ? '复制 PDF 链接' : '知道了',
            cancelButtonText: fallbackUrl ? '打开原文' : '取消',
            distinguishCancelAndClose: true,
            type: 'warning'
          }
        )
        if (pdfUrl) {
          await this.copyText(pdfUrl)
          this.$message.success('PDF 链接已复制')
        }
      } catch (action) {
        if (action === 'cancel' && fallbackUrl) {
          await this.openExternal(fallbackUrl)
        }
      }
    },
    relationProviderRef(work) {
      const refs = work && Array.isArray(work.providerRefs) ? work.providerRefs : []
      return refs.find(item => item && item.provider === this.provider) || refs[0] || null
    },
    relationPdfKey(work) {
      return this.screeningWorkKey(work)
    },
    isRelationPdfDownloading(work) {
      const key = this.relationPdfKey(work)
      return !!(key && this.relationPdfDownloadingKeys[key])
    },
    async downloadRelationPdf(work) {
      if (!work || !work.pdfUrl) {
        this.$message.warning('暂无可下载 PDF')
        return
      }
      const ref = this.relationProviderRef(work)
      if (!ref || !ref.provider || !ref.providerId) {
        this.$message.warning('暂无可下载 PDF')
        return
      }
      const key = this.relationPdfKey(work)
      if (key && this.relationPdfDownloadingKeys[key]) {
        return
      }
      if (key) {
        this.$set(this.relationPdfDownloadingKeys, key, true)
      }
      try {
        const result = await downloadScholarWorkPdf(ref.provider, ref.providerId)
        this.saveBlob(result.blob, result.filename || this.pdfFilenameForWork(work))
        this.$message.success('下载已开始')
      } catch (e) {
        await this.handlePdfDownloadFailure(work, e)
      } finally {
        if (key) {
          this.$delete(this.relationPdfDownloadingKeys, key)
        }
      }
    },
    openCitationDialog() {
      this.citationFormat = 'gbt7714'
      this.citationVisible = true
    },
    async copyCitation() {
      if (!this.citationText) {
        return
      }
      try {
        await this.copyText(this.citationText)
        this.$message.success('引用格式已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadCitation() {
      if (!this.citationText) {
        return
      }
      const extensionMap = {
        bibtex: 'bib',
        ris: 'ris',
        gbt7714: 'txt',
        apa: 'txt',
        mla: 'txt'
      }
      const typeMap = {
        bibtex: 'application/x-bibtex;charset=utf-8',
        ris: 'application/x-research-info-systems;charset=utf-8'
      }
      const filename = `${this.citationFilename()}.${extensionMap[this.citationFormat] || 'txt'}`
      this.downloadText(this.citationText, filename, typeMap[this.citationFormat] || 'text/plain;charset=utf-8')
    },
    async handleRelationCitationCommand(work, format) {
      if (!work || !format) {
        return
      }
      try {
        await this.copyText(formatCitation(work, format))
        this.$message.success('引用已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    toggleRelationExportPanel() {
      this.relationExportVisible = !this.relationExportVisible
      if (this.relationExportVisible) {
        this.activeRelationActionKey = ''
      }
    },
    runRelationExportCommand(format) {
      this.relationExportVisible = false
      this.exportRelationWorks(format)
    },
    relationActionKey(work, type) {
      const key = this.screeningWorkKey(work) || this.workIdentityKey(work)
      return key ? `${type}:${key}` : ''
    },
    isRelationActionPanelOpen(work, type) {
      const key = this.relationActionKey(work, type)
      return !!key && this.activeRelationActionKey === key
    },
    toggleRelationActionPanel(work, type) {
      const key = this.relationActionKey(work, type)
      if (!key) {
        return
      }
      this.activeRelationActionKey = this.activeRelationActionKey === key ? '' : key
      if (this.activeRelationActionKey) {
        this.relationExportVisible = false
      }
    },
    runRelationCitationCommand(work, format) {
      this.activeRelationActionKey = ''
      this.handleRelationCitationCommand(work, format)
    },
    runRelationScreeningCommand(work, command) {
      this.activeRelationActionKey = ''
      this.handleScreeningCommandForWork(work, command)
    },
    exportCitationLibrary(format) {
      const works = this.citationLibraryWorks()
      if (!works.length) {
        this.$message.warning('当前没有可导出的引用库')
        return
      }
      const extensionMap = {
        bibtex: 'bib',
        ris: 'ris',
        csv: 'csv',
        markdown: 'md'
      }
      const typeMap = {
        bibtex: 'application/x-bibtex;charset=utf-8',
        ris: 'application/x-research-info-systems;charset=utf-8',
        csv: 'text/csv;charset=utf-8',
        markdown: 'text/markdown;charset=utf-8'
      }
      if (!extensionMap[format]) {
        return
      }
      const content = format === 'markdown'
        ? this.citationLibraryMarkdown()
        : `${format === 'csv' ? '\uFEFF' : ''}${formatWorks(works, format, this.formatType)}`
      this.downloadText(content, `${this.citationFilename()}-citation-library.${extensionMap[format]}`, typeMap[format])
      this.$message.success(`已导出 ${works.length} 条引用库记录`)
    },
    citationLibrarySections() {
      const sections = [
        { key: 'current', title: '当前文献', items: this.work ? [this.work] : [] },
        { key: 'citedBy', title: '引用本文', items: this.relations.citedBy || [] },
        { key: 'references', title: '参考文献', items: this.relations.references || [] },
        { key: 'related', title: '相关文献', items: this.relations.related || [] }
      ]
      const seen = []
      return sections
        .map(section => ({
          ...section,
          items: (section.items || []).filter(work => {
            if (!work) {
              return false
            }
            const key = this.workIdentityKey(work)
            if (key && seen.includes(key)) {
              return false
            }
            if (key) {
              seen.push(key)
            }
            return true
          })
        }))
        .filter(section => section.items.length)
    },
    citationLibraryWorks() {
      return this.citationLibrarySections().reduce((items, section) => items.concat(section.items), [])
    },
    citationLibraryMarkdown() {
      const sections = this.citationLibrarySections()
      const lines = [
        `# ${this.work && this.work.title ? this.work.title : '文献'} - 引用库`,
        '',
        `共 ${this.citationLibraryWorks().length} 条记录`,
        '',
        '说明：引用库由当前文献详情和已加载的引用本文、参考文献、相关文献元数据生成；导入文献管理器前建议核对题名、作者、期刊、卷期页码和 DOI。',
        ''
      ]
      sections.forEach(section => {
        lines.push(`## ${section.title}`)
        section.items.forEach((item, index) => {
          const citation = formatCitation(item, 'gbt7714')
          const url = this.relationWorkUrl(item)
          lines.push(`### ${index + 1}. ${item.title || 'Untitled work'}`)
          if (citation) {
            lines.push(`- 引用：${citation}`)
          }
          lines.push(`- 年份：${item.publicationYear || '未知年份'}`)
          lines.push(`- 来源：${item.sourceName || '未知来源'}`)
          lines.push(`- 引用数：${this.formatNumber(item.citedByCount || 0)}`)
          if (item.doi) {
            lines.push(`- DOI：${this.doiUrl(item)}`)
          }
          if (url) {
            lines.push(`- 链接：${url}`)
          }
          lines.push('')
        })
      })
      return lines.join('\n')
    },
    workIdentityKey(work) {
      if (!work) {
        return ''
      }
      if (work.doi) {
        return `doi:${String(work.doi).toLowerCase()}`
      }
      if (work.workKey) {
        return `work:${work.workKey}`
      }
      const refs = work.providerRefs || []
      const ref = refs.find(item => item && item.providerId) || refs[0]
      if (ref && ref.providerId) {
        return `provider:${ref.provider || ''}:${ref.providerId}`
      }
      return `${work.title || ''}:${work.publicationYear || ''}`.toLowerCase()
    },
    screeningStorageKey() {
      return 'scholar-screening-marks-v1'
    },
    loadScreeningMarks() {
      try {
        const raw = window.localStorage.getItem(this.screeningStorageKey())
        const parsed = raw ? JSON.parse(raw) : {}
        this.screeningMarks = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
      } catch (e) {
        this.screeningMarks = {}
      }
    },
    persistScreeningMarks() {
      try {
        window.localStorage.setItem(this.screeningStorageKey(), JSON.stringify(this.screeningMarks || {}))
      } catch (e) {
        // 本地初筛是便捷能力，存储失败不影响文献详情浏览。
      }
    },
    firstProviderId(work) {
      const refs = work && Array.isArray(work.providerRefs) ? work.providerRefs : []
      const ref = refs.find(item => item && item.provider === this.provider) || refs[0]
      return ref && ref.providerId ? ref.providerId : ''
    },
    screeningWorkKey(work) {
      if (!work) {
        return ''
      }
      return work.workKey || this.firstProviderId(work) || work.doi || work.title || ''
    },
    screeningMarkForWork(work) {
      const key = this.screeningWorkKey(work)
      return key ? this.screeningMarks[key] || null : null
    },
    screeningWorkSnapshot(work) {
      if (!work) {
        return null
      }
      return {
        workKey: this.screeningWorkKey(work),
        title: work.title || '',
        doi: work.doi || '',
        year: work.publicationYear || '',
        source: work.sourceName || '',
        providerId: this.firstProviderId(work) || '',
        url: this.relationWorkUrl(work) || this.doiUrl(work) || ''
      }
    },
    screeningRelationContextForWork(work) {
      if (!work || !this.work || this.screeningWorkKey(work) === this.screeningWorkKey(this.work)) {
        return null
      }
      const targetKey = this.screeningWorkKey(work)
      const section = this.relationSections.find(item => {
        return (item.items || []).some(candidate => this.screeningWorkKey(candidate) === targetKey)
      })
      if (!section) {
        return null
      }
      return {
        relationKey: section.key,
        relationLabel: section.title,
        sourceWork: this.screeningWorkSnapshot(this.work)
      }
    },
    screeningDecisionText(decision) {
      const map = {
        include: '纳入',
        exclude: '排除',
        maybe: '可能相关',
        pending: '待定'
      }
      return map[decision] || '待定'
    },
    screeningReviewerName() {
      const user = this.getScholarAuthUser()
      return user && user.name ? this.cleanText(user.name) : ''
    },
    setScreeningMarkForWork(work, patch) {
      const key = this.screeningWorkKey(work)
      if (!key) {
        return
      }
      const previous = this.screeningMarks[key] || {}
      const mark = {
        decision: 'pending',
        excludeReason: '',
        notes: '',
        reviewer: this.screeningReviewerName(),
        ...previous,
        ...patch,
        work: this.screeningWorkSnapshot(work) || previous.work || null,
        relationContext: patch.relationContext || previous.relationContext || this.screeningRelationContextForWork(work),
        updatedAt: new Date().toISOString()
      }
      this.$set(this.screeningMarks, key, mark)
      this.persistScreeningMarks()
    },
    setScreeningMark(patch) {
      this.setScreeningMarkForWork(this.work, patch)
    },
    clearScreeningMarkForWork(work) {
      const key = this.screeningWorkKey(work)
      if (!key || !this.screeningMarks[key]) {
        return
      }
      this.$delete(this.screeningMarks, key)
      this.persistScreeningMarks()
      this.$message.success('已清除初筛标记')
    },
    clearScreeningMark() {
      this.clearScreeningMarkForWork(this.work)
    },
    async handleScreeningCommand(command) {
      return this.handleScreeningCommandForWork(this.work, command)
    },
    async handleScreeningCommandForWork(work, command) {
      if (!work || !command) {
        return
      }
      if (command === 'clear') {
        this.clearScreeningMarkForWork(work)
        return
      }
      if (command === 'exclude') {
        const current = this.screeningMarkForWork(work) || {}
        const result = await this.$prompt('填写排除原因，返回列表后也会进入筛选表导出。', '排除原因', {
          confirmButtonText: '保存',
          cancelButtonText: '取消',
          inputValue: current.excludeReason || '',
          inputPlaceholder: '例如：主题不相关、非研究论文、无全文等'
        }).catch(() => null)
        if (!result) {
          return
        }
        this.setScreeningMarkForWork(work, {
          decision: 'exclude',
          excludeReason: this.cleanText(result.value || ''),
          reviewer: this.screeningReviewerName()
        })
        this.$message.success('已标记为排除')
        return
      }
      if (command === 'note') {
        const current = this.screeningMarkForWork(work) || {}
        const result = await this.$prompt('填写初筛备注，返回列表后也会进入筛选表导出。', '初筛备注', {
          confirmButtonText: '保存',
          cancelButtonText: '取消',
          inputValue: current.notes || '',
          inputType: 'textarea',
          inputPlaceholder: '例如：方法相关，需阅读全文确认样本/指标'
        }).catch(() => null)
        if (!result) {
          return
        }
        this.setScreeningMarkForWork(work, {
          decision: current.decision || 'pending',
          notes: this.cleanText(result.value || ''),
          reviewer: this.screeningReviewerName()
        })
        this.$message.success('初筛备注已保存')
        return
      }
      const decisionMap = {
        include: 'include',
        maybe: 'maybe',
        pending: 'pending'
      }
      if (!decisionMap[command]) {
        return
      }
      const current = this.screeningMarkForWork(work) || {}
      this.setScreeningMarkForWork(work, {
        decision: decisionMap[command],
        excludeReason: command === 'include' ? '' : current.excludeReason || '',
        reviewer: this.screeningReviewerName()
      })
      this.$message.success(`已标记为${this.screeningDecisionText(decisionMap[command])}`)
    },
    isSaved(work) {
      return !!(work && work.workKey && this.savedWorks.some(item => item.work && item.work.workKey === work.workKey))
    },
    async loadSavedWorks() {
      if (!this.isScholarPersonalUser()) {
        this.savedWorks = []
        return
      }
      try {
        this.savedWorks = await listSavedScholarWorks()
      } catch (e) {
        this.savedWorks = []
      }
    },
    async toggleSavedWork() {
      if (!this.work || !this.work.workKey || this.savedLoading) {
        return
      }
      if (!this.requireScholarLogin()) {
        return
      }
      this.savedLoading = true
      try {
        if (this.isCurrentWorkSaved) {
          await deleteSavedScholarWork(this.work.workKey)
          this.savedWorks = this.savedWorks.filter(item => item.work && item.work.workKey !== this.work.workKey)
          this.$message.success('已取消收藏')
          return
        }
        const saved = await saveScholarWork(this.work, ['OA文献'], '')
        this.savedWorks = [saved].concat(this.savedWorks.filter(item => item.work && item.work.workKey !== saved.work.workKey))
        this.$message.success('已收藏文献')
      } catch (e) {
        this.$message.error(this.resolveErrorMessage(e, '收藏操作失败'))
      } finally {
        this.savedLoading = false
      }
    },
    currentDetailLink() {
      const query = {
        provider: this.provider,
        id: this.providerId
      }
      if (this.$route.query.q) {
        query.q = this.$route.query.q
      }
      const route = this.$router.resolve({ path: '/scholar/detail', query })
      const origin = window.location.origin || ''
      return `${origin}${route.href}`
    },
    workAuthorsText(work, limit = 12) {
      const authors = work && work.authors ? work.authors : []
      const names = authors.map(item => item && item.name).filter(Boolean)
      if (!names.length) {
        return '未知作者'
      }
      const displayed = names.slice(0, limit).join('、')
      return names.length > limit ? `${displayed} 等` : displayed
    },
    workTopicNames(work) {
      return (work && work.topics ? work.topics : [])
        .map(item => item && item.name)
        .filter(Boolean)
    },
    workKeywordNames() {
      return this.keywordChips
        .map(item => item && item.name)
        .filter(Boolean)
    },
    workNoteText() {
      const work = this.work || {}
      const authors = this.workAuthorsText(work)
      const topics = this.workTopicNames(work)
      const keywords = this.workKeywordNames()
      const biblio = this.biblioText(work)
      const sourceLine = [work.sourceName, biblio].filter(Boolean).join('，') || '未知来源'
      const primaryUrl = work.landingPageUrl || this.doiUrl(work) || this.providerUrl
      const citation = formatCitation(work, 'gbt7714')
      const lines = [
        `# ${work.title || 'Untitled work'}`,
        '',
        '## 基本信息',
        `- 引用格式：${citation || '暂无引用格式'}`,
        `- 作者：${authors}`,
        `- 年份：${work.publicationYear || '未知年份'}`,
        `- 来源：${sourceLine}`,
        `- 类型：${work.type ? this.formatType(work.type) : '未知类型'}`,
        `- 语言：${work.language ? this.languageText(work.language) : '未知语言'}`,
        `- 引用数：${this.formatNumber(work.citedByCount || 0)}`,
        `- 开放获取：${work.isOa ? this.oaStatusText(work.oaStatus) : '否'}`,
        `- DOI：${work.doi ? this.doiUrl(work) : '暂无 DOI'}`,
        `- 原文链接：${primaryUrl || '暂无链接'}`,
        `- PDF：${work.pdfUrl || '暂无 PDF 链接'}`,
        `- 平台详情：${this.currentDetailLink()}`,
        '',
        '## 摘要',
        work.abstractText || '暂无摘要',
        '',
        '## 主题',
        topics.length ? topics.map(item => `- ${item}`).join('\n') : '- 暂无主题',
        '',
        '## 关键词',
        keywords.length ? keywords.map(item => `- ${item}`).join('\n') : '- 暂无关键词'
      ]
      if (this.openAccessRows.length) {
        lines.push('', '## 开放获取位置')
        this.openAccessRows.forEach(row => {
          lines.push(`- ${[row.sourceName, row.tags.join('、'), row.versionText, row.licenseText].filter(Boolean).join(' · ')}`)
          lines.push(`  - ${row.urlType}：${row.url}`)
        })
      }
      lines.push('', '## 阅读记录', '- 关键结论：', '- 可引用点：', '- 待核对：')
      return lines.join('\n')
    },
    async copyWorkNote() {
      if (!this.work) {
        return
      }
      try {
        await this.copyText(this.workNoteText())
        this.$message.success('文献笔记已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadWorkNote() {
      if (!this.work) {
        return
      }
      this.downloadText(this.workNoteText(), `${this.citationFilename()}-note.md`, 'text/markdown;charset=utf-8')
      this.$message.success('文献笔记已导出')
    },
    handleResearchMaterialCommand(command) {
      if (command === 'copy-note') {
        this.copyWorkNote()
        return
      }
      if (command === 'download-note') {
        this.downloadWorkNote()
        return
      }
      if (command === 'copy-evidence') {
        this.copyEvidenceCard()
        return
      }
      if (command === 'download-evidence') {
        this.downloadEvidenceCard()
        return
      }
      if (command === 'copy-brief') {
        this.copyResearchBrief()
        return
      }
      if (command === 'download-brief') {
        this.downloadResearchBrief()
      }
    },
    handleDetailMoreCommand(command) {
      const value = String(command || '')
      if (value.startsWith('screening:')) {
        return this.handleScreeningCommand(value.slice('screening:'.length))
      }
      if (value.startsWith('library:')) {
        return this.exportCitationLibrary(value.slice('library:'.length))
      }
      if (value.startsWith('expand:')) {
        return this.handleExpandSearchCommand(value.slice('expand:'.length))
      }
      if (value.startsWith('material:')) {
        return this.handleResearchMaterialCommand(value.slice('material:'.length))
      }
      return null
    },
    toggleResearchActionGroup(group) {
      const nextOpen = !this.researchActionGroups[group]
      Object.keys(this.researchActionGroups).forEach(key => {
        this.$set(this.researchActionGroups, key, false)
      })
      if (nextOpen) {
        this.$set(this.researchActionGroups, group, true)
      }
    },
    runDetailMoreCommand(command) {
      this.researchActionsVisible = false
      return this.handleDetailMoreCommand(command)
    },
    markdownCell(value) {
      return String(value === null || value === undefined ? '' : value)
        .replace(/\|/g, '\\|')
        .replace(/\r?\n/g, '<br>')
    },
    evidenceStatusText(status) {
      const map = {
        ok: '可用',
        missing: '待补充',
        warn: '需谨慎'
      }
      return map[status] || status || ''
    },
    evidenceCardText() {
      const work = this.work || {}
      const lines = [
        `# ${work.title || 'Untitled work'} - 元数据证据卡`,
        '',
        '说明：证据卡只汇总当前详情页和三方接口已返回的元数据状态，不生成论文结论或质量评价。',
        '',
        `- 平台详情：${this.currentDetailLink()}`,
        `- 原文链接：${work.landingPageUrl || this.doiUrl(work) || this.providerUrl || '暂无链接'}`,
        `- 记录时间：${new Date().toLocaleString()}`,
        '',
        '| 项目 | 状态 | 取值 | 依据/提示 |',
        '| --- | --- | --- | --- |'
      ]
      this.evidenceRows.forEach(item => {
        lines.push(`| ${this.markdownCell(item.label)} | ${this.evidenceStatusText(item.status)} | ${this.markdownCell(item.value)} | ${this.markdownCell(item.hint)} |`)
      })
      lines.push('', '## 建议核对')
      this.evidenceRows
        .filter(item => item.status !== 'ok')
        .forEach(item => {
          lines.push(`- ${item.label}：${item.hint || item.value}`)
        })
      if (!this.evidenceRows.some(item => item.status !== 'ok')) {
        lines.push('- 当前接口元数据覆盖较完整，引用前仍建议核对原文、DOI、期刊/来源和 PDF 可访问性。')
      }
      return lines.join('\n')
    },
    async copyEvidenceCard() {
      if (!this.work) {
        return
      }
      try {
        await this.copyText(this.evidenceCardText())
        this.$message.success('元数据证据卡已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadEvidenceCard() {
      if (!this.work) {
        return
      }
      this.downloadText(this.evidenceCardText(), `${this.citationFilename()}-evidence.md`, 'text/markdown;charset=utf-8')
      this.$message.success('元数据证据卡已导出')
    },
    researchBriefText() {
      const work = this.work || {}
      const citation = formatCitation(work, 'gbt7714')
      const topics = (work.topics || []).map(topic => {
        const extra = [topic.field, topic.domain, this.topicDetailHint(topic)].filter(Boolean)
        return `${topic.name || topic.id}${extra.length ? `（${Array.from(new Set(extra)).join(' · ')}）` : ''}`
      }).filter(Boolean)
      const keywords = this.keywordChips
        .map(item => {
          const score = item.score !== undefined && item.score !== null ? `，相关度 ${this.formatDecimal(item.score, 3)}` : ''
          return `${item.name || item.id}${score}`
        })
        .filter(Boolean)
      const lines = [
        `# ${work.title || 'Untitled work'} - 调研线索`,
        '',
        '说明：本报告仅整理当前详情页和三方接口已返回的元数据、引用关系与开放获取位置，不生成论文结论或质量判断。',
        '',
        '## 基本信息',
        `- 引用格式：${citation || '暂无引用格式'}`,
        `- 作者：${this.workAuthorsText(work)}`,
        `- 年份：${work.publicationYear || '未知年份'}`,
        `- 来源：${[work.sourceName, this.biblioText(work)].filter(Boolean).join('，') || '未知来源'}`,
        `- 类型：${work.type ? this.formatType(work.type) : '未知类型'}`,
        `- 语言：${work.language ? this.languageText(work.language) : '未知语言'}`,
        `- 引用数：${this.formatNumber(work.citedByCount || 0)}`,
        `- 开放获取：${work.isOa ? this.oaStatusText(work.oaStatus) : '否'}`,
        `- DOI：${work.doi ? this.doiUrl(work) : '暂无 DOI'}`,
        `- 原文链接：${work.landingPageUrl || this.doiUrl(work) || this.providerUrl || '暂无链接'}`,
        `- 平台详情：${this.currentDetailLink()}`
      ]
      if (this.workQualityWarning) {
        lines.push(`- 风险标记：${this.workQualityWarning}`)
      }
      if (this.impactDetailText) {
        lines.push(`- 规范化影响：${this.impactDetailText}`)
      }
      if (this.collaborationText) {
        lines.push(`- 合作规模：${this.collaborationText}`)
      }
      if (this.indexedInLabels.length) {
        lines.push(`- 收录数据库：${this.indexedInLabels.join('、')}`)
      }
      if (this.externalIdentifiers.length) {
        lines.push('', '## 外部标识')
        this.externalIdentifiers.forEach(item => {
          lines.push(`- ${item.label}：${item.url || item.value}`)
        })
      }
      lines.push('', '## 元数据可用性', '', '| 项目 | 状态 | 取值 | 依据/提示 |', '| --- | --- | --- | --- |')
      this.evidenceRows.forEach(item => {
        lines.push(`| ${this.markdownCell(item.label)} | ${this.evidenceStatusText(item.status)} | ${this.markdownCell(item.value)} | ${this.markdownCell(item.hint)} |`)
      })
      lines.push('', '## 摘要', work.abstractText || '暂无摘要')
      lines.push('', '## 主题与关键词')
      lines.push('### 主题')
      lines.push(topics.length ? topics.map(item => `- ${item}`).join('\n') : '- 暂无主题')
      lines.push('', '### 关键词')
      lines.push(keywords.length ? keywords.map(item => `- ${item}`).join('\n') : '- 暂无关键词')
      if (this.meshTerms.length) {
        lines.push('', '### MeSH 主题词')
        this.meshTerms.forEach(term => {
          lines.push(`- ${this.meshTermText(term)}${term.majorTopic ? '（主要主题）' : ''}`)
        })
      }
      if (this.sustainableGoals.length) {
        lines.push('', '### 可持续发展目标')
        this.sustainableGoals.forEach(goal => {
          lines.push(`- ${goal.name || goal.id}`)
        })
      }
      if (this.grants.length || this.funders.length) {
        lines.push('', '## 资助信息')
        if (this.grants.length) {
          this.grants.forEach(grant => {
            lines.push(`- ${[grant.funderName || grant.funderId, grant.awardId].filter(Boolean).join(' · ') || '未命名资助'}`)
          })
        } else {
          this.funders.forEach(funder => {
            lines.push(`- ${funder.name || funder.id}`)
          })
        }
      }
      if (this.openAccessRows.length) {
        lines.push('', '## 开放获取位置')
        this.openAccessRows.forEach(row => {
          lines.push(`- ${[row.sourceName, row.tags.join('、'), row.versionText, row.licenseText].filter(Boolean).join(' · ') || '开放获取位置'}`)
          lines.push(`  - ${row.urlType}：${row.url}`)
        })
      }
      if (this.countsByYear.length) {
        lines.push('', '## 年度引用趋势')
        this.countsByYear.forEach(item => {
          lines.push(`- ${item.year || '-'}：${this.formatNumber(item.citedByCount || 0)}`)
        })
      }
      lines.push('', '## 关联文献线索')
      this.relationSections.forEach(section => {
        lines.push(`### ${section.title}`)
        lines.push(`- 已加载：${this.relationLoadedText(section)}`)
        if (!section.items.length) {
          lines.push('- 暂无可展示条目')
        } else {
          section.items.slice(0, 8).forEach((item, index) => {
            lines.push(this.relationBriefLine(item, index))
          })
        }
        lines.push('')
      })
      lines.push('## 待核对项')
      lines.push('- 作者、期刊/来源、卷期页码、DOI 与引用格式。')
      lines.push('- PDF 或开放获取位置是否为可访问全文。')
      lines.push('- 引用本文、参考文献、相关文献是否已加载到足够数量。')
      return lines.join('\n')
    },
    relationLoadedText(section) {
      const loaded = section && section.items ? section.items.length : 0
      const total = this.numberOrNull(section && section.total)
      return total === null ? `${this.formatNumber(loaded)} 条` : `${this.formatNumber(loaded)} / ${this.formatNumber(total)} 条`
    },
    relationBriefLine(work, index) {
      const citation = formatCitation(work, 'gbt7714')
      const url = this.relationWorkUrl(work)
      const parts = [
        `${index + 1}. ${citation || work.title || 'Untitled work'}`,
        `年份 ${work.publicationYear || '未知'}`,
        `来源 ${work.sourceName || '未知'}`,
        `引 ${this.formatNumber(work.citedByCount || 0)}`
      ]
      if (work.doi) {
        parts.push(`DOI ${work.doi}`)
      }
      if (url) {
        parts.push(url)
      }
      return `- ${parts.join('；')}`
    },
    bestOpenAccessLocationForWork(work) {
      const extra = work && work.extra ? work.extra : {}
      const candidates = [
        extra.bestOaLocation,
        extra.primaryLocation
      ].concat(Array.isArray(extra.locations) ? extra.locations : [])
      return candidates.find(item => item && (item.pdfUrl || item.landingPageUrl || item.version || item.license || item.sourceName || item.sourceType || item.sourceIsInDoaj)) || null
    },
    openAccessEvidenceForWork(work) {
      const location = this.bestOpenAccessLocationForWork(work)
      const extra = work && work.extra ? work.extra : {}
      return {
        version: location && location.version ? this.openVersionText(location.version) : '',
        license: location && location.license ? this.licenseText(location.license) : '',
        sourceName: location && location.sourceName ? location.sourceName : '',
        sourceType: location && location.sourceType ? this.sourceTypeText(location.sourceType) : '',
        isDoaj: !!(location && location.sourceIsInDoaj),
        hasFulltext: !!(extra && extra.hasFulltext),
        locationsCount: extra && extra.locationsCount !== null && extra.locationsCount !== undefined ? extra.locationsCount : ''
      }
    },
    openAccessEvidenceTextForWork(work) {
      const evidence = this.openAccessEvidenceForWork(work)
      const chips = []
      if (work && work.oaStatus) {
        chips.push(this.oaStatusText(work.oaStatus))
      }
      if (evidence.version && evidence.version !== '未知') {
        chips.push(evidence.version)
      }
      if (evidence.license && evidence.license !== '未知') {
        chips.push(evidence.license)
      }
      if (work && work.pdfUrl) {
        chips.push('PDF')
      }
      if (evidence.hasFulltext) {
        chips.push('全文可检索')
      }
      if (evidence.isDoaj) {
        chips.push('DOAJ')
      }
      if (evidence.locationsCount) {
        chips.push(`${this.formatNumber(evidence.locationsCount)} 个位置`)
      }
      return Array.from(new Set(chips.filter(Boolean))).join('; ')
    },
    async copyResearchBrief() {
      if (!this.work) {
        return
      }
      try {
        await this.copyText(this.researchBriefText())
        this.$message.success('调研线索已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadResearchBrief() {
      if (!this.work) {
        return
      }
      this.downloadText(this.researchBriefText(), `${this.citationFilename()}-research-brief.md`, 'text/markdown;charset=utf-8')
      this.$message.success('调研线索已导出')
    },
    exportRelationWorks(format) {
      const section = this.activeRelationSection
      const works = section && section.items ? section.items : []
      if (!works.length) {
        this.$message.warning('当前没有可导出的关联文献')
        return
      }
      const extensionMap = {
        bibtex: 'bib',
        ris: 'ris',
        csv: 'csv',
        screening: 'csv',
        gbt7714: 'txt',
        markdown: 'md'
      }
      const typeMap = {
        bibtex: 'application/x-bibtex;charset=utf-8',
        ris: 'application/x-research-info-systems;charset=utf-8',
        csv: 'text/csv;charset=utf-8',
        screening: 'text/csv;charset=utf-8',
        gbt7714: 'text/plain;charset=utf-8',
        markdown: 'text/markdown;charset=utf-8'
      }
      if (!extensionMap[format]) {
        return
      }
      const content = format === 'markdown'
        ? this.relationWorksMarkdown(section)
        : (format === 'screening'
          ? `\uFEFF${this.relationScreeningSheetCsv(section)}`
          : `${format === 'csv' ? '\uFEFF' : ''}${formatWorks(works, format, this.formatType)}`)
      const filename = `${this.citationFilename()}-${section.key}.${extensionMap[format]}`
      this.downloadText(content, filename, typeMap[format])
      this.$message.success(`已导出 ${works.length} 篇${section.title}`)
    },
    relationScreeningSheetCsv(section) {
      const target = section || this.activeRelationSection
      const works = target && target.items ? target.items : []
      const sourceTitle = this.work && this.work.title ? this.work.title : ''
      const headers = [
        'Screening Decision',
        'Exclude Reason',
        'Reviewer',
        'Notes',
        'Relation Type',
        'Source Work',
        'Title',
        'Authors',
        'Year',
        'Source',
        'Type',
        'Language',
        'Citations',
        'OA',
        'OA Version',
        'OA License',
        'OA Source',
        'OA Evidence',
        'Has PDF',
        'Has Abstract',
        'DOI',
        'PDF URL',
        'Landing URL',
        'Provider URL',
        'Abstract',
        'Topics'
      ]
      const rows = works.map(work => {
        const oaEvidence = this.openAccessEvidenceForWork(work)
        return [
          '',
          '',
          '',
          '',
          target ? target.title : '',
          sourceTitle,
          work.title || '',
          this.workAuthorsText(work, 20),
          work.publicationYear || '',
          work.sourceName || '',
          this.formatType(work.type) || '',
          work.language || '',
          work.citedByCount || 0,
          work.isOa ? 'yes' : 'no',
          oaEvidence.version || '',
          oaEvidence.license || '',
          [oaEvidence.sourceName, oaEvidence.sourceType].filter(Boolean).join(' · '),
          this.openAccessEvidenceTextForWork(work),
          work.pdfUrl ? 'yes' : 'no',
          work.abstractText ? 'yes' : 'no',
          work.doi || '',
          work.pdfUrl || '',
          work.landingPageUrl || '',
          this.relationWorkUrl(work),
          work.abstractText ? String(work.abstractText).replace(/\s+/g, ' ').trim() : '',
          (work.topics || []).map(item => item && item.name).filter(Boolean).join('; ')
        ]
      })
      return [headers].concat(rows).map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    relationWorkUrl(work) {
      const refs = work && work.providerRefs ? work.providerRefs : []
      const ref = refs.find(item => item && item.url) || refs[0]
      return work && (work.landingPageUrl || this.doiUrl(work) || (ref && ref.url)) || ''
    },
    relationWorksMarkdown(section) {
      const target = section || this.activeRelationSection
      const works = target && target.items ? target.items : []
      const lines = [
        `# ${this.work && this.work.title ? this.work.title : '文献'} - ${target ? target.title : '关联文献'}清单`,
        '',
        `共 ${works.length} 篇`,
        ''
      ]
      works.forEach((item, index) => {
        const authors = this.workAuthorsText(item, 6)
        const url = this.relationWorkUrl(item)
        lines.push(`## ${index + 1}. ${item.title || 'Untitled work'}`)
        lines.push(`- 作者：${authors}`)
        lines.push(`- 年份：${item.publicationYear || '未知年份'}`)
        lines.push(`- 来源：${item.sourceName || '未知来源'}`)
        lines.push(`- 类型：${item.type ? this.formatType(item.type) : '未知类型'}`)
        lines.push(`- 引用数：${this.formatNumber(item.citedByCount || 0)}`)
        if (item.doi) {
          lines.push(`- DOI：${this.doiUrl(item)}`)
        }
        if (url) {
          lines.push(`- 链接：${url}`)
        }
        if (item.abstractText) {
          lines.push(`- 摘要：${this.cleanText(item.abstractText).slice(0, 260)}${this.cleanText(item.abstractText).length > 260 ? '...' : ''}`)
        }
        lines.push('')
      })
      return lines.join('\n')
    },
    async copyRelationWorks() {
      const section = this.activeRelationSection
      const works = section && section.items ? section.items : []
      if (!works.length) {
        this.$message.warning('当前没有可复制的关联文献')
        return
      }
      try {
        await this.copyText(this.relationWorksMarkdown(section))
        this.$message.success(`已复制 ${works.length} 篇${section.title}`)
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    openAccessLocationTags(location, roles) {
      const tags = []
      const roleSet = new Set(roles || [])
      if (roleSet.has('best')) {
        tags.push('最佳')
      }
      if (roleSet.has('primary')) {
        tags.push('主位置')
      }
      if (location && location.pdfUrl) {
        tags.push('PDF')
      }
      if (location && location.sourceIsInDoaj) {
        tags.push('DOAJ')
      }
      if (location && location.sourceIsOa) {
        tags.push('OA 来源')
      }
      if (location && location.license) {
        tags.push('有许可证')
      }
      return tags.length ? tags : ['开放']
    },
    openAccessLocationScore(location, roles) {
      const roleSet = new Set(roles || [])
      let score = 0
      if (roleSet.has('best')) {
        score += 80
      }
      if (roleSet.has('primary')) {
        score += 40
      }
      if (location && location.pdfUrl) {
        score += 30
      }
      if (location && location.sourceIsInDoaj) {
        score += 12
      }
      if (location && location.license) {
        score += 8
      }
      if (location && location.version === 'publishedVersion') {
        score += 6
      }
      return score
    },
    openAccessLocationsMarkdown() {
      const lines = [
        `# ${this.work && this.work.title ? this.work.title : '文献'} - 开放获取位置`,
        '',
        '说明：开放获取位置来自三方接口返回的 best_oa_location、primary_location 和 locations 字段；链接可用性以三方站点实际访问为准。',
        '',
        '| 来源 | 标签 | 版本 | 许可证 | 链接类型 | URL |',
        '| --- | --- | --- | --- | --- | --- |'
      ]
      this.openAccessRows.forEach(row => {
        lines.push([
          row.sourceName,
          row.tags.join('、'),
          row.versionText || '-',
          row.licenseText || '-',
          row.urlType,
          row.url || ''
        ].map(value => this.markdownCell(value)).join(' | ').replace(/^/, '| ').replace(/$/, ' |'))
      })
      return lines.join('\n')
    },
    openAccessLocationsCsv() {
      const headers = ['Title', 'Source', 'Source Type', 'Tags', 'Version', 'License', 'URL Type', 'URL']
      const rows = this.openAccessRows.map(row => [
        this.work && this.work.title ? this.work.title : '',
        row.sourceName,
        row.sourceType,
        row.tags.join('; '),
        row.versionText || '',
        row.licenseText || '',
        row.urlType,
        row.url || ''
      ])
      return [headers].concat(rows).map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    async copyOpenAccessLocations() {
      if (!this.openAccessRows.length) {
        this.$message.warning('暂无开放获取位置')
        return
      }
      try {
        await this.copyText(this.openAccessLocationsMarkdown())
        this.$message.success('开放获取位置已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadOpenAccessLocations() {
      if (!this.openAccessRows.length) {
        this.$message.warning('暂无开放获取位置')
        return
      }
      this.downloadText(`\uFEFF${this.openAccessLocationsCsv()}`, `${this.citationFilename()}-oa-locations.csv`, 'text/csv;charset=utf-8')
      this.$message.success('开放获取位置已导出')
    },
    async copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return
      }
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', 'readonly')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      const success = document.execCommand('copy')
      document.body.removeChild(textarea)
      if (!success) {
        throw new Error('copy failed')
      }
    },
    downloadText(content, filename, type) {
      const blob = new Blob([content], { type: type || 'text/plain;charset=utf-8' })
      this.saveBlob(blob, filename)
    },
    citationFilename() {
      const title = this.work && this.work.title ? this.work.title : 'scholar-citation'
      return title.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'scholar-citation'
    },
    saveBlob(blob, filename) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename || this.defaultPdfFilename()
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
    },
    defaultPdfFilename() {
      const title = this.work && this.work.title ? this.work.title : 'scholar-work'
      const safeTitle = title.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim()
      return `${safeTitle || 'scholar-work'}.pdf`
    },
    pdfFilenameForWork(work) {
      const title = work && work.title ? work.title : 'scholar-work'
      const safeTitle = title.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim()
      return `${safeTitle || 'scholar-work'}.pdf`
    },
    citationTrendCsv() {
      const rows = [
        ['title', 'year', 'cited_by_count'],
        ...this.countsByYear.map(item => [
          this.work && this.work.title ? this.work.title : '',
          item.year || '',
          item.citedByCount || 0
        ])
      ]
      return rows.map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    async copyCitationTrend() {
      if (!this.countsByYear.length) {
        return
      }
      const lines = [
        `${this.work && this.work.title ? this.work.title : '文献'} 年度引用趋势`,
        ...this.countsByYear.map(item => `${item.year || '-'}：${this.formatNumber(item.citedByCount || 0)}`)
      ]
      try {
        await this.copyText(lines.join('\n'))
        this.$message.success('年度引用数据已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadCitationTrend() {
      if (!this.countsByYear.length) {
        return
      }
      this.downloadText(`\uFEFF${this.citationTrendCsv()}`, `${this.citationFilename()}-citations.csv`, 'text/csv;charset=utf-8')
      this.$message.success('年度引用数据已导出')
    },
    citationPercent(item) {
      const value = Number(item && item.citedByCount ? item.citedByCount : 0)
      return `${Math.max(4, Math.round((value / this.maxCitationCount) * 100))}%`
    },
    formatLocationVersion(version) {
      const map = {
        publishedVersion: '发表版',
        acceptedVersion: '接收稿',
        submittedVersion: '投稿稿'
      }
      return map[version] || version || ''
    },
    sourceTypeText(type) {
      const map = {
        journal: '期刊',
        repository: '仓储',
        book: '图书',
        ebook: '电子书',
        conference: '会议',
        preprint: '预印本',
        report: '报告'
      }
      return map[type] || type || ''
    },
    licenseText(license) {
      return license ? String(license).replace(/-/g, ' ').toUpperCase() : ''
    },
    doiUrl(work) {
      return work && work.doi ? `https://doi.org/${work.doi}` : ''
    },
    biblioText(work) {
      const biblio = work && work.extra && work.extra.biblio ? work.extra.biblio : {}
      const volume = this.cleanText(biblio.volume)
      const issue = this.cleanText(biblio.issue)
      const firstPage = this.cleanText(biblio.firstPage)
      const lastPage = this.cleanText(biblio.lastPage)
      const volumeIssue = volume && issue ? `${volume}(${issue})` : (volume || (issue ? `(${issue})` : ''))
      const pages = firstPage && lastPage && firstPage !== lastPage ? `${firstPage}-${lastPage}` : (firstPage || lastPage)
      return [volumeIssue, pages].filter(Boolean).join(': ')
    },
    cleanText(value) {
      return String(value === null || value === undefined ? '' : value).replace(/\s+/g, ' ').trim()
    },
    openRelatedWork(work) {
      const ref = work && work.providerRefs && work.providerRefs.length ? work.providerRefs[0] : null
      if (!ref || !ref.provider || !ref.providerId) {
        return
      }
      const query = {
        provider: ref.provider,
        id: ref.providerId
      }
      if (this.$route.query.q) {
        query.q = this.$route.query.q
      }
      this.$router.push({ path: '/scholar/detail', query })
    },
    handleExpandSearchCommand(key) {
      const option = this.expandSearchOptions.find(item => item.key === key)
      if (!option) {
        return
      }
      this.openExpandedSearch(option.query)
    },
    openExpandedSearch(query) {
      const nextQuery = this.compactQuery(query)
      if (!Object.keys(nextQuery).length) {
        return
      }
      this.$router.push({
        path: '/scholar',
        query: nextQuery
      })
    },
    compactQuery(query) {
      const next = {}
      Object.keys(query || {}).forEach(key => {
        const value = query[key]
        if (value !== null && value !== undefined && String(value).trim() !== '') {
          next[key] = value
        }
      })
      return next
    },
    searchCitingWorks() {
      if (!this.providerId) {
        return
      }
      this.openExpandedSearch({
        citesWorkId: this.providerId,
        citesWorkName: this.work && this.work.title ? this.work.title : this.providerId,
        sort: 'cited'
      })
    },
    openEntityDetail(entityType, id, name) {
      if (!id) {
        return
      }
      this.$router.push({
        path: '/scholar/entity',
        query: {
          provider: this.provider,
          entityType,
          id,
          name: name || '',
          q: this.$route.query.q || ''
        }
      })
    },
    openGrantFunder(grant) {
      if (!grant || !grant.funderId) {
        return
      }
      this.openEntityDetail('funders', grant.funderId, grant.funderName || grant.funderId)
    },
    openKeyword(keyword) {
      if (!keyword || !keyword.id) {
        return
      }
      this.openEntityDetail('keywords', keyword.id, keyword.name || keyword.id)
    },
    goSearchWithTopic(topic) {
      if (!topic || !topic.id) {
        return
      }
      this.$router.push({
        path: '/scholar',
        query: {
          q: this.$route.query.q || '',
          topicId: topic.id,
          topicName: topic.name || ''
        }
      })
    },
    authorInstitutions(author) {
      const institutions = author && author.institutions ? author.institutions : []
      return institutions.map(item => item.name).filter(Boolean).slice(0, 4).join('、')
    },
    metric(entity, key) {
      return entity && entity.metrics ? entity.metrics[key] : null
    },
    sourceMetric(key) {
      return this.metric(this.sourceDetail, key)
    },
    sourceExtra(key) {
      return this.sourceDetail && this.sourceDetail.extra ? this.sourceDetail.extra[key] : null
    },
    sourceExtraText(entity) {
      if (!entity || !entity.extra) {
        return ''
      }
      return [entity.extra.type, entity.extra.issnL, entity.extra.hostOrganizationName].filter(Boolean).join(' · ')
    },
    topicDetailHint(topic) {
      const detail = topic && topic.id ? this.entityDetails.topics[topic.id] : null
      if (!detail || !detail.extra) {
        return topic && topic.domain ? topic.domain : ''
      }
      return [detail.extra.field, detail.extra.domain].filter(Boolean).join(' · ')
    },
    meshTermText(term) {
      if (!term) {
        return ''
      }
      return [term.descriptorName, term.qualifierName].filter(Boolean).join(' / ')
    },
    formatInstitutionType(type) {
      const typeMap = {
        education: '教育机构',
        healthcare: '医疗机构',
        company: '企业',
        archive: '档案/存储库',
        nonprofit: '非营利组织',
        government: '政府机构',
        facility: '科研设施'
      }
      return typeMap[type] || type || '-'
    },
    providerName(provider) {
      const providerMap = {
        openalex: 'OpenAlex',
        crossref: 'Crossref',
        semantic_scholar: 'Semantic Scholar',
        pubmed: 'PubMed',
        unpaywall: 'Unpaywall',
        arxiv: 'arXiv'
      }
      return providerMap[provider] || provider || 'Provider'
    },
    oaStatusText(status) {
      const statusMap = {
        gold: '金色 OA',
        green: '绿色 OA',
        bronze: '青铜 OA',
        hybrid: '混合 OA',
        diamond: '钻石 OA',
        closed: '非开放'
      }
      return statusMap[status] || status
    },
    languageText(language) {
      const languageMap = {
        en: '英语',
        zh: '中文',
        es: '西班牙语',
        fr: '法语',
        de: '德语',
        ja: '日语',
        ko: '韩语',
        pt: '葡萄牙语',
        ru: '俄语'
      }
      return languageMap[language] || language
    },
    formatType(type) {
      const typeMap = {
        article: '论文',
        preprint: '预印本',
        'book-chapter': '图书章节',
        book: '书籍',
        'proceedings-article': '会议论文',
        'conference-paper': '会议论文',
        'conference-abstract': '会议摘要',
        review: '综述'
      }
      return typeMap[type] || type
    },
    indexedInText(value) {
      const key = String(value || '').toLowerCase()
      const map = {
        crossref: 'Crossref',
        pubmed: 'PubMed',
        datacite: 'DataCite',
        doaj: 'DOAJ',
        arxiv: 'arXiv',
        mag: 'MAG'
      }
      return map[key] || value || ''
    },
    csvCell(value) {
      const text = String(value === null || value === undefined ? '' : value)
      return `"${text.replace(/"/g, '""')}"`
    },
    formatNumber(value) {
      const number = Number(value || 0)
      return number.toLocaleString()
    },
    numberOrNull(value) {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const number = Number(value)
      return Number.isFinite(number) ? number : null
    },
    relationTabCount(section) {
      const total = this.numberOrNull(section && section.total)
      return this.formatNumber(total === null ? ((section && section.items && section.items.length) || 0) : total)
    },
    formatDecimal(value, digits = 2) {
      const number = Number(value)
      if (!Number.isFinite(number)) {
        return ''
      }
      return number.toFixed(digits).replace(/\.?0+$/, '')
    },
    resolveErrorMessage(error, fallback) {
      const data = error && error.response && error.response.data
      return (data && (data.message || data.error)) || fallback
    }
  }
}
</script>

<style lang="less" scoped src="../assets/v2/css/trans/common.less"/>
<style lang="less">
.scholar-expand-menu.el-dropdown-menu {
  width: 340px;
  max-width: calc(100vw - 32px);
  padding: 6px 0;
  border: 1px solid #dce8f5;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(31, 45, 61, 0.14);
}

.scholar-expand-menu.el-dropdown-menu .el-dropdown-menu__item {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: auto;
  padding: 9px 14px;
  color: #38566e;
  line-height: 19px;
  white-space: normal;
}

.scholar-expand-menu.el-dropdown-menu .el-dropdown-menu__item:hover,
.scholar-expand-menu.el-dropdown-menu .el-dropdown-menu__item:focus {
  background: var(--primary-alpha-08);
}

.scholar-expand-menu .scholar-expand-option,
.scholar-expand-menu .scholar-expand-option strong,
.scholar-expand-menu .scholar-expand-option em {
  display: block;
  min-width: 0;
  max-width: 100%;
}

.scholar-expand-menu .scholar-expand-option strong,
.scholar-expand-menu .scholar-expand-option em {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-expand-menu .scholar-expand-option strong {
  color: #24445f;
  font-size: 13px;
  font-weight: 700;
}

.scholar-expand-menu .scholar-expand-option em {
  margin-top: 3px;
  color: #7a8da0;
  font-size: 12px;
  font-style: normal;
}

.scholar-detail-more-menu.el-dropdown-menu {
  width: 420px;
  max-width: calc(100vw - 32px);
  max-height: ~"min(560px, calc(100vh - 120px))";
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #dce8f5;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(31, 45, 61, 0.14);
}

.scholar-detail-more-menu.el-dropdown-menu .el-dropdown-menu__item {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 34px;
  height: auto;
  margin: 1px 0;
  padding: 7px 10px;
  border-radius: 4px;
  color: #38566e;
  font-size: 13px;
  line-height: 18px;
  white-space: normal;
}

.scholar-detail-more-menu.el-dropdown-menu .el-dropdown-menu__item:hover,
.scholar-detail-more-menu.el-dropdown-menu .el-dropdown-menu__item:focus {
  background: var(--primary-alpha-08);
}

.scholar-detail-more-menu.el-dropdown-menu .el-dropdown-menu__item.is-disabled.scholar-more-section {
  min-height: 26px;
  margin: 7px 0 3px;
  padding: 8px 10px 4px;
  border-top: 1px solid #eef3f8;
  border-radius: 0;
  background: #ffffff;
  color: #8aa0b4;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  cursor: default;
}

.scholar-detail-more-menu.el-dropdown-menu .el-dropdown-menu__item.is-disabled.scholar-more-section:first-child {
  margin-top: 0;
  padding-top: 4px;
  border-top: 0;
}

.scholar-detail-more-menu .scholar-more-option {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 3px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.scholar-detail-more-menu .scholar-more-option strong,
.scholar-detail-more-menu .scholar-more-option em {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-detail-more-menu .scholar-more-option strong {
  color: #24445f;
  font-size: 13px;
  font-weight: 700;
}

.scholar-detail-more-menu .scholar-more-option em {
  color: #7a8da0;
  font-size: 12px;
  font-style: normal;
}

.scholar-detail-actions-popover.el-popover {
  max-width: calc(100vw - 32px);
  padding: 8px;
  border: 1px solid #dce8f5;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(31, 45, 61, 0.14);
  box-sizing: border-box;
}

.scholar-research-actions-panel {
  max-height: ~"min(560px, calc(100vh - 120px))";
  overflow-y: auto;
}

.scholar-research-group-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 36px;
  padding: 8px 10px;
  border: 0;
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: #24445f;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.scholar-research-group-toggle + .scholar-research-group-toggle,
.scholar-research-group-body + .scholar-research-group-toggle {
  margin-top: 6px;
}

.scholar-research-group-toggle:hover,
.scholar-research-group-toggle:focus {
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-research-group-toggle span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.scholar-research-group-body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  padding: 8px 0 2px;
}

.scholar-research-action-item {
  min-width: 0;
  min-height: 32px;
  padding: 7px 9px;
  border: 1px solid #e1edf5;
  border-radius: 4px;
  background: #ffffff;
  color: #38566e;
  font-size: 13px;
  line-height: 18px;
  text-align: left;
  cursor: pointer;
}

.scholar-research-action-item:hover,
.scholar-research-action-item:focus {
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-research-action-item:disabled {
  color: #a6b4c3;
  cursor: not-allowed;
  background: #f7fafc;
}

.scholar-research-action-item.is-two-line {
  display: block;
}

.scholar-research-action-item.is-two-line strong,
.scholar-research-action-item.is-two-line em {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-research-action-item.is-two-line strong {
  color: inherit;
  font-weight: 700;
}

.scholar-research-action-item.is-two-line em {
  margin-top: 3px;
  color: #7a8da0;
  font-size: 12px;
  font-style: normal;
}

@media screen and (max-width: 768px) {
  .scholar-expand-menu.el-dropdown-menu,
  .scholar-detail-more-menu.el-dropdown-menu,
  .scholar-detail-actions-popover.el-popover {
    width: calc(100vw - 24px) !important;
    max-width: calc(100vw - 24px);
    box-sizing: border-box;
  }

  .scholar-detail-more-menu.el-dropdown-menu,
  .scholar-research-actions-panel {
    max-height: calc(100vh - 150px);
    max-height: calc(100dvh - 150px);
  }

  .scholar-research-group-body {
    grid-template-columns: minmax(0, 1fr);
  }

  .scholar-citation-dialog .el-dialog {
    width: calc(100vw - 24px) !important;
    max-width: 760px;
    margin: 20px auto 0 !important;
  }

  .scholar-citation-dialog .el-dialog__header {
    padding: 16px 18px 10px;
  }

  .scholar-citation-dialog .el-dialog__body {
    max-height: calc(100vh - 178px);
    max-height: calc(100dvh - 178px);
    padding: 14px 16px;
    overflow-y: auto;
  }

  .scholar-citation-dialog .el-dialog__footer {
    padding: 10px 16px calc(14px + env(safe-area-inset-bottom));
  }

  .scholar-citation-dialog .scholar-citation-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .scholar-citation-dialog .scholar-citation-tabs .el-radio-button {
    flex: 1 1 calc(50% - 3px);
  }

  .scholar-citation-dialog .scholar-citation-tabs .el-radio-button__inner {
    width: 100%;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    box-shadow: none;
  }
}
</style>
<style lang="less" scoped>
.scholar-detail-page {
  min-width: 1200px;
  min-height: 100vh;
  background: var(--bg-page);
  font-family: Microsoft YaHei;
}

.scholar-detail-shell {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: var(--bg-page);
}

.scholar-detail-shell::before {
  content: "";
  position: absolute;
  top: 70px;
  left: 0;
  right: 0;
  height: 1040px;
  pointer-events: none;
  background: url(../assets/chat/bg.png) top center / 1920px 1148px no-repeat;
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 58%, rgba(0, 0, 0, 0.45) 78%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 0%, #000 58%, rgba(0, 0, 0, 0.45) 78%, transparent 100%);
}

.scholar-trans-header {
  position: relative;
  z-index: 1;
  min-width: 1200px;
  height: 70px;
  margin-top: -1px;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.18);
}

.scholar-trans-header .box_2 {
  width: 1200px;
  height: 59px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.scholar-trans-header .image_1 {
  width: 104px;
  height: 28px;
  cursor: pointer;
}

.scholar-trans-header .text_1 {
  height: 28px;
  margin: 0 0 0 119px;
  display: flex;
  align-items: center;
  gap: 32px;
  color: var(--foreground-secondary);
  font-size: 16px;
  line-height: 24px;
  white-space: nowrap;
}

.scholar-trans-header .text_1 span {
  cursor: pointer;
}

.scholar-trans-header .text_1 .active {
  color: var(--primary);
  font-weight: 700;
}

.scholar-trans-header .thumbnail_1 {
  width: 14px;
  height: 14px;
  margin: 6px 0 0 20px;
  cursor: pointer;
}

.scholar-trans-header .text_2 {
  max-width: 92px;
  height: 24px;
  margin: 7px 0 0 8px;
  overflow: hidden;
  color: #666666;
  font-size: 16px;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.scholar-detail-main {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 0 auto;
  padding: 34px 24px 58px;
  box-sizing: border-box;
}

.scholar-detail-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 16px;
}

.scholar-detail-layout.with-toc {
  display: grid;
  grid-template-columns: 154px minmax(0, 1fr);
  gap: 20px;
  align-items: flex-start;
}

.scholar-detail-toc {
  position: sticky;
  top: 88px;
  padding: 12px 0;
  border-top: 1px solid #dfe9f2;
  border-bottom: 1px solid #dfe9f2;
}

.scholar-detail-toc button {
  display: block;
  width: 100%;
  height: 30px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: #5f7285;
  font-size: 13px;
  line-height: 30px;
  text-align: left;
  cursor: pointer;
}

.scholar-detail-toc button:hover,
.scholar-detail-toc button:focus {
  color: var(--primary);
  font-weight: 700;
}

@media screen and (max-width: 1180px) {
  .scholar-detail-layout.with-toc {
    display: block;
  }

  .scholar-detail-toc {
    position: static;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
    padding: 0;
    border: 0;
  }

  .scholar-detail-toc button {
    width: auto;
    height: 28px;
    padding: 0 10px;
    border-radius: 4px;
    background: var(--primary-alpha-08);
    color: var(--primary);
  }
}

.scholar-back-btn {
  height: 36px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #cfdce8;
  border-radius: 4px;
  background: #ffffff;
  color: #355268;
  font-size: 14px;
  cursor: pointer;
}

.scholar-provider-tip {
  color: #6c7b8a;
  font-size: 13px;
}

.scholar-detail-card {
  min-width: 0;
  min-height: 520px;
  padding: 34px 36px 44px;
  border: none;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 8px 26px rgba(35, 73, 104, 0.06);
  box-sizing: border-box;
}

.scholar-detail-error {
  margin-bottom: 18px;
}

.scholar-quality-alert {
  margin: 18px 0 0;
}

.scholar-detail-head {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding-bottom: 18px;
  border-bottom: 1px solid #e5edf4;
}

.scholar-detail-title-row {
  display: block;
  width: 100%;
  min-width: 0;
  margin: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
  color: #1f2b38;
  font-size: 25px;
  font-weight: 700;
  line-height: 36px;
  letter-spacing: 0;
}

.scholar-detail-badges-row,
.scholar-topic-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.scholar-oa-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: #e7fbef;
  color: #12a260;
  font-size: 13px;
  font-weight: 700;
}

.scholar-soft-badge,
.scholar-topic-list span {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 9px;
  border-radius: 999px;
  background: #edf7fd;
  color: var(--primary);
  font-size: 13px;
}

.scholar-screening-badge {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 9px;
  border-radius: 999px;
  background: #f4f7fb;
  color: #657489;
  font-size: 13px;
  font-weight: 700;
}

.scholar-screening-badge.is-include {
  background: #ecfdf3;
  color: #078649;
}

.scholar-screening-badge.is-exclude {
  background: #fff1f2;
  color: #be123c;
}

.scholar-screening-badge.is-maybe {
  background: #fff7ed;
  color: #c46a00;
}

.scholar-screening-badge.is-pending {
  background: #f4f7fb;
  color: #657489;
}

.scholar-detail-actions {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding-top: 0;
}

.scholar-detail-actions .el-button {
  min-width: 92px;
  margin-left: 0;
}

.scholar-detail-actions .scholar-download-btn {
  font-weight: 700;
}

.scholar-detail-actions .scholar-icon-btn {
  width: 38px;
  height: 34px;
  min-width: 38px;
  padding: 0;
  flex-shrink: 0;
  border: 1px solid var(--primary);
  border-radius: 6px;
  background: var(--primary);
  box-shadow: 0 4px 10px rgba(22, 130, 191, 0.22);
  color: #ffffff;
  font-size: 18px;
  line-height: 34px;
}

.scholar-detail-actions .scholar-icon-btn:hover,
.scholar-detail-actions .scholar-icon-btn:focus {
  border-color: var(--primary);
  background: var(--primary);
  color: #ffffff;
}

.scholar-detail-actions .scholar-icon-btn i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.scholar-download-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.scholar-citation-btn,
.scholar-screening-btn,
.scholar-library-btn,
.scholar-expand-btn,
.scholar-note-btn,
.scholar-more-btn,
.scholar-save-btn {
  min-width: 76px !important;
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-more-btn {
  min-width: 124px !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.scholar-citation-btn:hover,
.scholar-citation-btn:focus,
.scholar-screening-btn:hover,
.scholar-screening-btn:focus,
.scholar-library-btn:hover,
.scholar-library-btn:focus,
.scholar-expand-btn:hover,
.scholar-expand-btn:focus,
.scholar-note-btn:hover,
.scholar-note-btn:focus,
.scholar-more-btn:hover,
.scholar-more-btn:focus,
.scholar-save-btn:hover,
.scholar-save-btn:focus {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

/deep/ .scholar-expand-menu {
  width: 340px;
  max-width: calc(100vw - 32px);
  padding: 6px 0;
  border: 1px solid #dce8f5;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(31, 45, 61, 0.14);
}

/deep/ .scholar-expand-menu .el-dropdown-menu__item {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: auto;
  padding: 9px 14px;
  color: #38566e;
  line-height: 19px;
  white-space: normal;
}

/deep/ .scholar-expand-menu .el-dropdown-menu__item:hover,
/deep/ .scholar-expand-menu .el-dropdown-menu__item:focus {
  background: var(--primary-alpha-08);
}

/deep/ .scholar-expand-menu .scholar-expand-option,
/deep/ .scholar-expand-menu .scholar-expand-option strong,
/deep/ .scholar-expand-menu .scholar-expand-option em {
  display: block;
  min-width: 0;
  max-width: 100%;
}

/deep/ .scholar-expand-menu .scholar-expand-option strong,
/deep/ .scholar-expand-menu .scholar-expand-option em {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/deep/ .scholar-expand-menu .scholar-expand-option strong {
  color: #24445f;
  font-size: 13px;
  font-weight: 700;
}

/deep/ .scholar-expand-menu .scholar-expand-option em {
  margin-top: 3px;
  color: #7a8da0;
  font-size: 12px;
  font-style: normal;
}

.scholar-availability-panel {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0 22px;
  margin: 0 0 18px;
  padding: 14px 0 16px;
  border-top: 1px solid #edf3f7;
  border-bottom: 1px solid #edf3f7;
}

.scholar-availability-item {
  position: relative;
  min-width: 0;
  padding: 8px 0 8px 14px;
}

.scholar-availability-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 13px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #a7b7c5;
}

.scholar-availability-item.is-ok::before {
  background: #1fb972;
}

.scholar-availability-item.is-warn::before {
  background: #d99b24;
}

.scholar-availability-item.is-missing::before {
  background: #9aa8b4;
}

.scholar-availability-item span,
.scholar-availability-item strong,
.scholar-availability-item em {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-availability-item span {
  color: #718294;
  font-size: 12px;
  line-height: 18px;
}

.scholar-availability-item strong {
  margin-top: 2px;
  color: #223142;
  font-size: 14px;
  line-height: 20px;
}

.scholar-availability-item em {
  margin-top: 2px;
  color: #8a98a6;
  font-size: 12px;
  font-style: normal;
  line-height: 18px;
}

.scholar-detail-metrics {
  margin: 0 0 28px;
  padding: 16px 0 18px;
  display: grid;
  grid-template-columns: 130px 130px minmax(0, 1fr);
  border-bottom: 1px solid #e5edf4;
  background: transparent;
}

.scholar-detail-metrics.has-impact {
  grid-template-columns: 120px 120px 120px minmax(0, 1fr);
}

.scholar-detail-metrics div {
  min-height: auto;
  padding: 0 22px 0 0;
  border-right: none;
  box-sizing: border-box;
}

.scholar-detail-metrics div:last-child {
  border-right: none;
}

.scholar-detail-metrics strong {
  display: block;
  max-width: 100%;
  overflow: hidden;
  color: #223142;
  font-size: 18px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-text-link {
  display: inline;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: #1c76aa;
  font: inherit;
  line-height: inherit;
  text-align: left;
  cursor: pointer;
}

.scholar-text-link.strong {
  font-weight: inherit;
}

.scholar-text-link:hover {
  color: #0e628d;
}

.scholar-detail-metrics span {
  display: block;
  margin-top: 6px;
  color: #7a8591;
  font-size: 12px;
}

.scholar-evidence-section {
  padding-top: 20px;
}

.scholar-evidence-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 30px;
}

.scholar-evidence-row {
  position: relative;
  display: grid;
  grid-template-columns: 92px 112px minmax(0, 1fr);
  align-items: center;
  min-height: 34px;
  padding-left: 14px;
  border-bottom: 1px dashed #e3ebf2;
  color: #34465a;
  font-size: 13px;
}

.scholar-evidence-row span {
  color: #5d6f81;
}

.scholar-evidence-row strong {
  color: #243447;
  font-size: 13px;
  font-weight: 700;
}

.scholar-evidence-row em {
  overflow: hidden;
  color: #7c8d9d;
  font-style: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-evidence-row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #2fb36d;
  transform: translateY(-50%);
}

.scholar-evidence-row.is-missing::before {
  background: #d9a019;
}

.scholar-evidence-row.is-warn::before {
  background: #d44949;
}

.scholar-evidence-note {
  margin: 12px 0 0;
  color: #7b8b9a;
  font-size: 12px;
  line-height: 20px;
}

.scholar-source-panel {
  padding: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  border: none;
  border-radius: 0;
  background: transparent;
}

.scholar-source-panel strong {
  display: block;
  color: #2d343d;
  font-size: 15px;
  line-height: 23px;
}

.scholar-source-panel span {
  display: block;
  margin-top: 4px;
  color: #708090;
  font-size: 13px;
}

.scholar-detail-section {
  scroll-margin-top: 92px;
  margin-top: 0;
  padding: 24px 0;
  border-bottom: 1px solid #e8eef4;
}

.scholar-detail-section:last-child {
  border-bottom: none;
}

.scholar-detail-section h2 {
  margin: 0 0 14px;
  color: #27313c;
  font-size: 17px;
  font-weight: 700;
}

.scholar-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.scholar-section-head h2 {
  margin-bottom: 0;
}

.scholar-section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.scholar-section-actions button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 9px;
  border: 1px solid #d3e5f1;
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  cursor: pointer;
}

.scholar-section-actions button:hover,
.scholar-section-actions button:focus {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
}

.scholar-location-table {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e2ebf3;
}

.scholar-location-table button {
  display: grid;
  grid-template-columns: minmax(170px, 1.2fr) minmax(150px, 1.1fr) 82px 104px 58px;
  align-items: center;
  gap: 14px;
  min-height: 42px;
  padding: 8px 0;
  border: 0;
  border-bottom: 1px dashed #e1e8ef;
  background: transparent;
  color: #34465a;
  text-align: left;
  cursor: pointer;
}

.scholar-location-table button:hover,
.scholar-location-table button:focus {
  background: rgba(22, 130, 191, 0.04);
}

.scholar-location-source {
  min-width: 0;
}

.scholar-location-source strong,
.scholar-location-source em {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-location-source strong {
  color: #223142;
  font-size: 14px;
  line-height: 20px;
}

.scholar-location-source em {
  margin-top: 2px;
  color: #8191a1;
  font-size: 12px;
  font-style: normal;
}

.scholar-location-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.scholar-location-badges em,
.scholar-location-url {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: #edf7fd;
  color: var(--primary);
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
}

.scholar-location-url {
  background: var(--primary);
  color: #ffffff;
}

.scholar-location-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.scholar-location-list button {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) 44px;
  align-items: center;
  gap: 14px;
  min-height: 34px;
  padding: 0;
  border: 0;
  border-bottom: 1px dashed #e1e8ef;
  background: transparent;
  color: #34465a;
  text-align: left;
  cursor: pointer;
}

.scholar-location-list button:last-child {
  border-bottom: 0;
}

.scholar-location-list strong {
  overflow: hidden;
  color: #1f2b38;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-location-list span {
  overflow: hidden;
  color: #708090;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-location-list em {
  display: inline-flex;
  height: 22px;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: var(--primary);
  color: #ffffff;
  font-size: 12px;
  font-style: normal;
}

.scholar-author-list {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px 24px;
}

.scholar-author-item {
  min-height: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  box-sizing: border-box;
  max-width: 300px;
}

.scholar-author-item strong {
  display: block;
  color: #1f6f9d;
  font-size: 14px;
  line-height: 20px;
}

.scholar-author-item span {
  display: block;
  margin-top: 3px;
  overflow: hidden;
  color: #708090;
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-entity-mini,
.scholar-entity-stats {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.scholar-entity-mini {
  margin-top: 8px;
}

.scholar-entity-mini em,
.scholar-entity-stats span {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0;
  border-radius: 0;
  background: transparent;
  color: var(--primary);
  font-size: 12px;
  font-style: normal;
  line-height: 20px;
}

.scholar-entity-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.scholar-entity-card {
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  box-sizing: border-box;
}

.scholar-entity-card strong {
  display: block;
  color: #2d343d;
  font-size: 14px;
  line-height: 22px;
}

.scholar-entity-card p {
  margin: 3px 0 6px;
  color: #708090;
  font-size: 12px;
  line-height: 18px;
}

.scholar-abstract-full {
  margin: 0;
  color: #34465a;
  font-size: 15px;
  line-height: 1.95;
}

.scholar-muted {
  margin: 0;
  color: #8795a3;
  font-size: 14px;
}

.scholar-topic-detail-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.scholar-keyword-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.scholar-keyword-list button {
  min-height: 24px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  border: none;
  border-radius: 999px;
  background: #eef7fc;
  color: var(--primary);
  font-size: 13px;
  line-height: 24px;
  cursor: pointer;
}

.scholar-keyword-list button:disabled {
  color: var(--primary);
  cursor: default;
}

.scholar-keyword-list button:not(:disabled):hover {
  background: #dff1fb;
}

.scholar-mesh-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.scholar-mesh-list span {
  min-height: 24px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  background: #eef7fc;
  color: var(--primary);
  font-size: 13px;
  line-height: 24px;
}

.scholar-mesh-list span.major {
  background: #eaf8f0;
  color: #169a5a;
  font-weight: 700;
}

.scholar-mesh-list em {
  color: inherit;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  opacity: 0.78;
}

.scholar-topic-detail-list button {
  max-width: 330px;
  min-height: 0;
  padding: 0 10px;
  border: none;
  border-radius: 999px;
  background: #eef7fc;
  text-align: left;
  cursor: pointer;
}

.scholar-topic-detail-list strong,
.scholar-relation-item strong {
  display: block;
  overflow: hidden;
  color: #1c76aa;
  font-size: 13px;
  line-height: 26px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-topic-detail-list span,
.scholar-topic-detail-list em {
  display: none;
  margin-top: 0;
  color: #708090;
  font-size: 12px;
  font-style: normal;
  line-height: 18px;
}

.scholar-field-list {
  margin: 0;
}

.scholar-field-list div {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  gap: 12px;
  margin-bottom: 10px;
  color: #536273;
  font-size: 14px;
  line-height: 22px;
}

.scholar-field-list dt {
  color: #8795a3;
}

.scholar-field-list dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.scholar-field-list dd a {
  color: var(--primary);
  text-decoration: none;
}

.scholar-indexed-list {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
}

.scholar-indexed-list em {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
}

.scholar-warning-text {
  color: #b54708;
  font-weight: 700;
}

.scholar-citation-bars {
  max-width: 520px;
}

.scholar-citation-row {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) 70px;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  color: #536273;
  font-size: 13px;
}

.scholar-citation-row em {
  height: 7px;
  overflow: hidden;
  border-radius: 4px;
  background: #e7eef5;
}

.scholar-citation-row i {
  display: block;
  height: 100%;
  border-radius: 4px;
  background: var(--primary);
}

.scholar-citation-row strong {
  color: #708090;
  font-size: 13px;
  font-weight: 400;
  text-align: right;
}

.scholar-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.scholar-chip-list span,
.scholar-chip-list button {
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  padding: 0 10px;
  border: 0;
  border-radius: 999px;
  background: #eef7fc;
  color: var(--primary);
  font-size: 13px;
  font-family: inherit;
}

.scholar-chip-list button {
  cursor: pointer;
}

.scholar-chip-list button:disabled {
  cursor: default;
  opacity: 0.72;
}

.scholar-relation-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.scholar-relation-header h2 {
  margin-bottom: 0;
}

.scholar-relation-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.scholar-relation-actions button {
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 13px;
  cursor: pointer;
}

.scholar-relation-actions button:hover {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
}

.scholar-relation-export-btn {
  height: 28px;
  padding: 0 10px;
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 13px;
}

.scholar-relation-export-btn:hover,
.scholar-relation-export-btn:focus {
  border-color: var(--primary);
  color: var(--primary-dark);
  background: var(--primary-alpha-08);
}

.scholar-relation-export-btn.active,
.scholar-relation-action-btn.active,
.scholar-relation-screening-btn.active {
  border-color: var(--primary);
  color: var(--primary-dark);
  background: var(--primary-alpha-08);
}

.scholar-relation-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  border-bottom: 1px solid #e4edf4;
}

.scholar-relation-tabs button {
  position: relative;
  height: 34px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: #5d7186;
  font-size: 14px;
  cursor: pointer;
}

.scholar-relation-tabs button.active {
  color: var(--primary);
  font-weight: 700;
}

.scholar-relation-tabs button.active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  background: var(--primary);
}

.scholar-relation-tabs em {
  margin-left: 5px;
  color: #8a9aaa;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
}

.scholar-relation-panel {
  display: flex;
  flex-direction: column;
}

.scholar-relation-export-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  margin: 0 0 12px;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
  box-sizing: border-box;
}

.scholar-relation-export-panel strong,
.scholar-relation-action-panel strong {
  display: block;
  margin: 0 0 8px;
  color: #24445f;
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
}

.scholar-relation-export-panel > div,
.scholar-relation-action-panel > div {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.scholar-relation-export-panel button,
.scholar-relation-action-panel button {
  min-width: 0;
  min-height: 30px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: #38566e;
  font-size: 12px;
  line-height: 17px;
  text-align: left;
  cursor: pointer;
}

.scholar-relation-export-panel button:hover,
.scholar-relation-export-panel button:focus,
.scholar-relation-action-panel button:hover,
.scholar-relation-action-panel button:focus {
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-relation-action-panel button:disabled {
  color: #a6b4c3;
  cursor: not-allowed;
  background: #f5f8fa;
}

.scholar-relation-summary {
  margin: 0 0 8px;
  color: #7b8da0;
  font-size: 12px;
}

.scholar-relation-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
  min-height: 54px;
  margin-bottom: 0;
  padding: 12px 0;
  position: relative;
  border: none;
  border-bottom: 1px dashed #e1e8ef;
  border-radius: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.scholar-relation-main {
  min-width: 0;
  flex: 1 1 360px;
}

.scholar-relation-item:hover strong {
  color: var(--primary);
}

.scholar-relation-item span {
  display: block;
  margin-top: 4px;
  color: #708090;
  font-size: 12px;
  line-height: 18px;
}

.scholar-relation-item-actions {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 8px;
}

.scholar-relation-action-panel {
  flex: 1 1 100%;
  min-width: 0;
  padding: 10px;
  border: 1px solid #e4eef6;
  border-radius: 4px;
  background: var(--primary-alpha-08);
  box-sizing: border-box;
  cursor: default;
}

.scholar-relation-action-btn {
  height: 26px;
  padding: 0 8px;
  border: 1px solid #d5e7f3;
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  line-height: 24px;
  cursor: pointer;
}

.scholar-relation-action-btn.primary {
  border-color: var(--primary);
  background: var(--primary);
  color: #ffffff;
}

.scholar-relation-action-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.scholar-relation-action-btn:not(:disabled):hover,
.scholar-relation-action-btn:not(:disabled):focus {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-relation-action-btn.primary:not(:disabled):hover,
.scholar-relation-action-btn.primary:not(:disabled):focus {
  border-color: var(--primary);
  background: var(--primary);
  color: #ffffff;
}

.scholar-relation-screening-btn {
  height: 26px;
  padding: 0 8px;
  border: 1px solid #d5e7f3;
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  line-height: 24px;
  cursor: pointer;
}

.scholar-relation-screening-btn:hover,
.scholar-relation-screening-btn:focus {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
}

.scholar-relation-screening-btn i {
  margin-left: 2px;
  font-size: 12px;
}

.scholar-relation-screening-badge {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  margin-left: 6px;
  padding: 0 7px;
  border-radius: 10px;
  background: #f4f7fb;
  color: #657489;
  font-size: 12px;
  font-style: normal;
  line-height: 20px;
  vertical-align: 1px;
}

.scholar-relation-screening-badge.is-include {
  background: #ecfdf3;
  color: #078649;
}

.scholar-relation-screening-badge.is-exclude {
  background: #fff1f2;
  color: #be123c;
}

.scholar-relation-screening-badge.is-maybe {
  background: #fff7ed;
  color: #c46a00;
}

.scholar-relation-screening-badge.is-pending {
  background: #f4f7fb;
  color: #657489;
}

.scholar-relation-go-icon {
  color: #a1b0bd;
  font-size: 14px;
}

.scholar-citation-title {
  margin-bottom: 14px;
  color: #202938;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.scholar-citation-tabs {
  margin-bottom: 14px;
}

.scholar-citation-text {
  min-height: 126px;
  max-height: 260px;
  margin: 0;
  padding: 14px 16px;
  overflow: auto;
  border: 1px solid #dfeaf4;
  border-radius: 6px;
  background: #f8fbfe;
  color: #26384c;
  font-family: Consolas, Menlo, Monaco, monospace;
  font-size: 13px;
  line-height: 22px;
  white-space: pre-wrap;
  word-break: break-word;
}

.scholar-citation-note {
  margin: 10px 0 0;
  color: #8a98a7;
  font-size: 13px;
  line-height: 20px;
}

.scholar-citation-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

@media screen and (max-width: 768px) {
  .scholar-detail-page,
  .scholar-detail-shell,
  .scholar-detail-main {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .scholar-detail-page,
  .scholar-detail-shell {
    overflow-x: hidden;
  }

  .scholar-detail-shell::before {
    top: 56px;
    height: 720px;
    background-size: auto 720px;
  }

  .scholar-detail-main {
    margin: 0;
    padding: 16px 12px calc(32px + env(safe-area-inset-bottom));
  }

  .scholar-detail-toolbar {
    margin-bottom: 12px;
  }

  .scholar-back-btn {
    min-height: 36px;
  }

  .scholar-detail-layout,
  .scholar-detail-layout.with-toc {
    display: block;
    width: 100%;
    min-width: 0;
  }

  .scholar-detail-toc {
    position: static;
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    width: 100%;
    margin: 0 0 12px;
    padding: 0 0 4px;
    overflow-x: auto;
    border: 0;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .scholar-detail-toc::-webkit-scrollbar {
    display: none;
  }

  .scholar-detail-toc button {
    flex: 0 0 auto;
    width: auto;
    min-height: 32px;
    height: 32px;
    padding: 0 11px;
    border-radius: 16px;
    background: var(--primary-alpha-08);
    color: var(--primary);
    line-height: 32px;
    white-space: nowrap;
  }

  .scholar-detail-card {
    width: 100%;
    min-width: 0;
    min-height: 360px;
    padding: 18px 14px 28px;
    border-radius: 6px;
  }

  .scholar-detail-head {
    gap: 10px;
    padding-bottom: 14px;
  }

  .scholar-detail-title-row {
    font-size: 21px;
    line-height: 30px;
  }

  .scholar-detail-badges-row,
  .scholar-topic-list {
    gap: 6px;
  }

  .scholar-detail-actions {
    align-items: stretch;
    gap: 8px;
  }

  .scholar-detail-actions .el-button {
    flex: 1 1 calc(50% - 4px);
    min-width: 0 !important;
    min-height: 36px;
    height: auto;
    margin-left: 0;
    padding: 8px 10px;
    white-space: normal;
  }

  .scholar-detail-actions .scholar-icon-btn {
    flex: 0 0 42px;
    width: 42px;
    min-width: 42px !important;
    height: 36px;
    padding: 0;
  }

  .scholar-detail-actions .scholar-more-btn {
    min-width: 0 !important;
  }

  .scholar-availability-panel {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
    margin-bottom: 14px;
    padding: 8px 0;
  }

  .scholar-availability-item {
    padding: 8px 0 8px 14px;
    border-bottom: 1px dashed #e3ebf2;
  }

  .scholar-availability-item:last-child {
    border-bottom: 0;
  }

  .scholar-availability-item span,
  .scholar-availability-item strong,
  .scholar-availability-item em {
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
  }

  .scholar-detail-metrics,
  .scholar-detail-metrics.has-impact {
    grid-template-columns: minmax(0, 1fr);
    margin-bottom: 16px;
    padding: 8px 0 10px;
  }

  .scholar-detail-metrics > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 0;
    padding: 9px 0;
    border-bottom: 1px dashed #e3ebf2;
  }

  .scholar-detail-metrics > div:last-child {
    border-bottom: 0;
  }

  .scholar-detail-metrics strong {
    min-width: 0;
    font-size: 16px;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .scholar-detail-metrics span {
    flex: 0 0 auto;
    margin-top: 0;
  }

  .scholar-detail-section {
    scroll-margin-top: 68px;
    padding: 20px 0;
  }

  .scholar-detail-section h2 {
    font-size: 16px;
  }

  .scholar-section-head,
  .scholar-relation-header,
  .scholar-source-panel {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .scholar-section-actions,
  .scholar-relation-actions {
    flex-wrap: wrap;
    width: 100%;
    gap: 8px;
  }

  .scholar-section-actions button,
  .scholar-relation-actions button {
    flex: 1 1 calc(50% - 4px);
    min-height: 32px;
    height: auto;
    justify-content: center;
    padding: 6px 8px;
  }

  .scholar-evidence-list {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }

  .scholar-evidence-row {
    grid-template-columns: 76px minmax(66px, auto) minmax(0, 1fr);
    gap: 6px;
    min-height: 40px;
    padding-left: 12px;
    font-size: 12px;
  }

  .scholar-evidence-row em {
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .scholar-location-table button {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 10px;
    padding: 12px 0;
  }

  .scholar-location-source,
  .scholar-location-badges {
    grid-column: 1 / -1;
  }

  .scholar-location-source strong,
  .scholar-location-source em {
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .scholar-location-url {
    justify-self: start;
  }

  .scholar-location-list button {
    grid-template-columns: minmax(0, 1fr);
    gap: 5px;
    min-height: 54px;
    padding: 8px 0;
  }

  .scholar-location-list strong,
  .scholar-location-list span {
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .scholar-location-list em {
    justify-self: start;
    min-width: 44px;
    padding: 0 8px;
  }

  .scholar-author-list {
    gap: 14px;
  }

  .scholar-author-item {
    width: 100%;
    max-width: none;
  }

  .scholar-topic-detail-list button {
    max-width: 100%;
  }

  .scholar-topic-detail-list strong,
  .scholar-relation-item strong {
    overflow: visible;
    line-height: 22px;
    text-overflow: clip;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .scholar-field-list div {
    grid-template-columns: 72px minmax(0, 1fr);
    gap: 8px;
  }

  .scholar-citation-bars {
    max-width: 100%;
  }

  .scholar-citation-row {
    grid-template-columns: 42px minmax(0, 1fr) 58px;
    gap: 7px;
  }

  .scholar-relation-tabs {
    align-items: stretch;
    flex-wrap: wrap;
    gap: 4px 10px;
  }

  .scholar-relation-tabs button {
    min-height: 34px;
    height: auto;
    padding: 6px 3px;
  }

  .scholar-relation-export-panel > div,
  .scholar-relation-action-panel > div {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .scholar-relation-item {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
    padding: 14px 0;
  }

  .scholar-relation-main {
    flex-basis: auto;
    width: 100%;
  }

  .scholar-relation-item-actions {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 7px;
  }

  .scholar-relation-action-btn,
  .scholar-relation-screening-btn {
    flex: 1 1 auto;
    min-height: 30px;
    height: auto;
    line-height: 18px;
    padding-top: 5px;
    padding-bottom: 5px;
  }

  .scholar-relation-go-icon {
    align-self: center;
    margin-left: auto;
  }

  .scholar-citation-title {
    overflow-wrap: anywhere;
  }

  .scholar-citation-actions {
    flex-wrap: wrap;
  }

  .scholar-citation-actions .el-button {
    flex: 1 1 calc(50% - 5px);
    margin-left: 0;
  }
}

@media screen and (max-width: 380px) {
  .scholar-detail-main {
    padding-left: 8px;
    padding-right: 8px;
  }

  .scholar-detail-card {
    padding-left: 11px;
    padding-right: 11px;
  }

  .scholar-detail-actions .el-button:not(.scholar-icon-btn) {
    flex-basis: 100%;
  }

  .scholar-evidence-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 3px;
    padding: 8px 0 8px 12px;
  }

  .scholar-location-table button,
  .scholar-relation-export-panel > div,
  .scholar-relation-action-panel > div {
    grid-template-columns: minmax(0, 1fr);
  }

  .scholar-location-source,
  .scholar-location-badges {
    grid-column: auto;
  }
}

</style>
